import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield,
  Bell,
  Save,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  Edit,
  X,
  Eye,
  EyeOff,
  Gauge,
  Rocket,
  TrendingUp,
  MoreVertical,
  Upload,
  Palette,
  QrCode,
  ShieldCheck,
  RotateCw,
  Users,
  UserCheck,
  ShieldOff,
  Download,
  CheckCircle2,
  XCircle,
  Clock3,
} from "lucide-react";
import "../styles/admin-base.css";
import "../styles/admin-settings.css";
import "../../User Side/styles/digital-id.css";

import client from "../../src/api/client";
import { flattenEmploymentOptionRows } from "../../src/data/employmentOptions";
import { useNavigate } from 'react-router-dom';
import ConfidenceBar from "../components/ai/ConfidenceBar";
import PasswordRequirements from "../components/PasswordRequirements";
import PasswordAuthenticationSettings from "../components/PasswordAuthenticationSettings";
import SudoModeModal from "../components/SudoModeModal";
import { normalizeAppearancePreferences } from "../lib/appearancePreferences";
import useSudoMode from "../hooks/useSudoMode";
import { getDepartmentAccountsSettings, createDepartmentAccount, updateDepartmentAccount, deleteDepartmentAccount, resetDepartmentAccountMfa,
  getUnionAffiliations, addUnionAffiliation, updateAffiliationStatus, updateAffiliationColorTheme, uploadAffiliationLogo,
  getUnionCompanies, addUnionCompany, updateCompanyStatus as updateUnionCompanyStatus,
  getUnionNames, addUnionName, updateUnionNameStatus,
  getUnionPositions, addUnionPosition, updatePositionStatus,
} from '../api/admin';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';
import { COLOR_THEMES } from '../../src/utils/unionBranding';
import defaultIdLogo from '@assets/ALUzon Logo.png';

const ADMIN_STATUS_OPTIONS = ["active", "suspended"];

const DEPT_ACCOUNT_STATUS_OPTIONS = ["active", "suspended", "disabled"];
const DEPARTMENT_OPTIONS = ["legal", "medical", "education", "emergency"];

const toTitleCase = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }
  const text = String(value).trim();
  if (!text) {
    return "—";
  }
  if (text === "—") {
    return text;
  }
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildDeptFormDefaults = () => ({
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
  department: "legal",
  status: "active",
});

const SESSION_TIMEOUT_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
];

const LOGIN_ATTEMPT_OPTIONS = [
  { value: 3, label: "3 attempts" },
  { value: 5, label: "5 attempts" },
  { value: 8, label: "8 attempts" },
  { value: 10, label: "10 attempts" },
];

const DEFAULT_INACTIVITY_THRESHOLD_DAYS = 30;

const normalizeInactivityThresholdDays = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_INACTIVITY_THRESHOLD_DAYS;
  }
  return Math.min(Math.max(Math.floor(parsed), 1), 365);
};

const buildSecurityDefaults = () => ({
  requireTwoFactor: true,
  sessionTimeoutMinutes: 30,
  loginAttemptLimit: 5,
  auditLoggingEnabled: true,
  inactivityThresholdDays: DEFAULT_INACTIVITY_THRESHOLD_DAYS,
});

const normalizeSecurityControls = (payload = {}) => {
  const defaults = buildSecurityDefaults();
  const parsedTimeout = Number(payload.sessionTimeoutMinutes);
  const parsedAttempts = Number(payload.loginAttemptLimit);
  return {
    requireTwoFactor: payload.requireTwoFactor !== undefined
      ? Boolean(payload.requireTwoFactor)
      : defaults.requireTwoFactor,
    sessionTimeoutMinutes: Number.isFinite(parsedTimeout) && parsedTimeout > 0
      ? parsedTimeout
      : defaults.sessionTimeoutMinutes,
    loginAttemptLimit: Number.isFinite(parsedAttempts) && parsedAttempts > 0
      ? parsedAttempts
      : defaults.loginAttemptLimit,
    auditLoggingEnabled: payload.auditLoggingEnabled !== undefined
      ? Boolean(payload.auditLoggingEnabled)
      : defaults.auditLoggingEnabled,
    inactivityThresholdDays: normalizeInactivityThresholdDays(payload.inactivityThresholdDays),
  };
};

const buildAdminFormDefaults = () => ({
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
  status: "active",
  roles: [],
});

const buildAiAutomationDefaults = () => ({
  autoAssign: true,
  autoResolve: false,
  auditLogging: true,
  suggestionDiff: true,
  confidenceThreshold: 0.85,
  enableActiveLearning: false,
});

const buildAiMetricsDefaults = () => ({
  autoAssignRate: 0,
  avgConfidence: 0,
  overrideRate: 0,
});

const buildAiRolloutDefaults = () => [];

const normalizeAutomationControls = (controls = {}) => {
  const defaults = buildAiAutomationDefaults();
  return {
    autoAssign: controls.autoAssign !== undefined ? Boolean(controls.autoAssign) : defaults.autoAssign,
    autoResolve: controls.autoResolve !== undefined ? Boolean(controls.autoResolve) : defaults.autoResolve,
    auditLogging: controls.auditLogging !== undefined ? Boolean(controls.auditLogging) : defaults.auditLogging,
    suggestionDiff: controls.suggestionDiff !== undefined ? Boolean(controls.suggestionDiff) : defaults.suggestionDiff,
    enableActiveLearning: controls.enableActiveLearning !== undefined ? Boolean(controls.enableActiveLearning) : defaults.enableActiveLearning,
    confidenceThreshold: Number.isFinite(Number(controls.confidenceThreshold)) ? Number(controls.confidenceThreshold) : defaults.confidenceThreshold,
  };
};

const normalizeMetrics = (metrics = {}) => {
  const defaults = buildAiMetricsDefaults();
  const coercePercent = (value, fallback) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return Math.round(parsed);
    }
    return fallback;
  };
  const coerceRatio = (value, fallback) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.min(Math.max(parsed, 0), 1);
    }
    return fallback;
  };
  return {
    autoAssignRate: coercePercent(metrics.autoAssignRate, defaults.autoAssignRate),
    avgConfidence: coerceRatio(metrics.avgConfidence, defaults.avgConfidence),
    overrideRate: coercePercent(metrics.overrideRate, defaults.overrideRate),
  };
};

const normalizeRollouts = (rollouts = []) => {
  if (!Array.isArray(rollouts)) {
    return buildAiRolloutDefaults();
  }
  return rollouts.map((entry, index) => {
    const coverageRaw = Number(entry?.coverage);
    const coverage = Number.isFinite(coverageRaw)
      ? Math.min(Math.max(coverageRaw > 1 ? coverageRaw / 100 : coverageRaw, 0), 1)
      : 0;
    return {
      id: entry?.id ?? `model-${index}`,
      name: entry?.name ?? "Untitled model",
      capabilities: Array.isArray(entry?.capabilities) ? entry.capabilities : [],
      coverage,
      lastUpdate: entry?.lastUpdate ?? "",
    };
  });
};

const validatePassword = (password) => {
  if (!password || password.length < 8) {
    return "Password must be at least 8 characters long.";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter.";
  }
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter.";
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must contain at least one special character.";
  }
  return null;
};

const EXCLUDED_ROLE_CODES = new Set(["dues_admin", "finance_admin"]);

const isExcludedRole = (role) => {
  const code = String(role?.code ?? "").trim().toLowerCase();
  if (code && (EXCLUDED_ROLE_CODES.has(code) || /dues|finance/.test(code))) {
    return true;
  }
  const name = String(role?.name ?? "").trim().toLowerCase();
  return /dues|finance/.test(name);
};

const normalizeRoleId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeUnionOptionText = (value, fallback = "—") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/i;

const normalizeHexColor = (value, fallback = "#DC2626") => {
  const text = String(value ?? "").trim();
  return HEX_COLOR_PATTERN.test(text) ? text.toUpperCase() : fallback;
};

const hexToRgb = (hex) => {
  const safeHex = normalizeHexColor(hex);
  const normalized = safeHex.slice(1);
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));
  const toHex = (value) => clamp(value).toString(16).padStart(2, "0").toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hslToRgb = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;

  const chroma = (1 - Math.abs(2 * light - 1)) * sat;
  const segment = hue / 60;
  const secondary = chroma * (1 - Math.abs((segment % 2) - 1));
  const match = light - chroma / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (segment >= 0 && segment < 1) {
    rPrime = chroma;
    gPrime = secondary;
  } else if (segment < 2) {
    rPrime = secondary;
    gPrime = chroma;
  } else if (segment < 3) {
    gPrime = chroma;
    bPrime = secondary;
  } else if (segment < 4) {
    gPrime = secondary;
    bPrime = chroma;
  } else if (segment < 5) {
    rPrime = secondary;
    bPrime = chroma;
  } else {
    rPrime = chroma;
    bPrime = secondary;
  }

  return {
    r: (rPrime + match) * 255,
    g: (gPrime + match) * 255,
    b: (bPrime + match) * 255,
  };
};

const relativeLuminance = ({ r, g, b }) => {
  const channel = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastRatio = (hexA, hexB) => {
  const lumA = relativeLuminance(hexToRgb(hexA));
  const lumB = relativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
};

const colorDistance = (hexA, hexB) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
};

const extractThemeStops = (theme) => {
  const matches = String(theme?.headerGradient ?? "").match(/#[0-9A-Fa-f]{6}/g) ?? [];
  const start = normalizeHexColor(matches[0], "#DC2626");
  const end = normalizeHexColor(matches[1], "#B91C1C");
  return { start, end };
};

const getThemeKeyFromGradient = (startHex, endHex) => {
  const start = normalizeHexColor(startHex);
  const end = normalizeHexColor(endHex);
  let bestKey = "red";
  let bestScore = Number.POSITIVE_INFINITY;

  Object.entries(COLOR_THEMES).forEach(([key, theme]) => {
    const stops = extractThemeStops(theme);
    const score = colorDistance(start, stops.start) + colorDistance(end, stops.end);
    if (score < bestScore) {
      bestScore = score;
      bestKey = key;
    }
  });

  return bestKey;
};

const generateVibrantGradientStops = () => {
  for (let attempt = 0; attempt < 42; attempt += 1) {
    const startHue = Math.floor(Math.random() * 360);
    const hueOffset = 95 + Math.floor(Math.random() * 140);
    const endHue = (startHue + hueOffset) % 360;

    const startSaturation = 72 + Math.floor(Math.random() * 24);
    const endSaturation = 72 + Math.floor(Math.random() * 24);
    const startLightness = 46 + Math.floor(Math.random() * 16);
    const endLightness = 18 + Math.floor(Math.random() * 20);

    const start = rgbToHex(hslToRgb(startHue, startSaturation, startLightness));
    const end = rgbToHex(hslToRgb(endHue, endSaturation, endLightness));

    if (contrastRatio(start, end) >= 1.75 && colorDistance(start, end) >= 130) {
      return { start, end };
    }
  }

  return { start: "#F97316", end: "#1D4ED8" };
};

const buildBackupManagementDefaults = () => ({
  enabled: true,
  scheduleType: "interval_days",
  intervalDays: 3,
  preferredScope: "full",
  historyCutoffAt: null,
  retention: {
    dailyDays: 14,
    weeklyDays: 56,
    monthlyDays: 186,
  },
});

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("user-management");
  const [systemSettings, setSystemSettings] = useState({});
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState({ name: '', code: '', description: '', industry: '', status: 'Active' });
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState('create');
  const [companyForm, setCompanyForm] = useState({ id: null, name: '', code: '', description: '', industry: '', status: 'Active' });
  const [companyPendingId, setCompanyPendingId] = useState(null);
  const navigate = useNavigate();

  const [unions, setUnions] = useState([]);
  const [unionPositions, setUnionPositions] = useState(['President','Vice President','Secretary','Treasurer','Auditor','Board Member','Member']);
  const [newPosition, setNewPosition] = useState('');
  // Union modal states
  const [unionModalOpen, setUnionModalOpen] = useState(false);
  const [unionModalMode, setUnionModalMode] = useState('create');
  const [unionForm, setUnionForm] = useState({ id: null, name: '', president: '', members: [] });
  const [unionPendingId, setUnionPendingId] = useState(null);
  const [unionMembersModalOpen, setUnionMembersModalOpen] = useState(false);
  const [activeUnionForMembers, setActiveUnionForMembers] = useState(null);
  const [unionMembersLocal, setUnionMembersLocal] = useState([]);
  const [memberForm, setMemberForm] = useState({ id: null, name: '', email: '' });
  const [memberEditingId, setMemberEditingId] = useState(null);
  const [unionMembershipRows, setUnionMembershipRows] = useState([]);
  const [totalApprovedMembersForUnions, setTotalApprovedMembersForUnions] = useState(0);
  const [unionFilters, setUnionFilters] = useState({
    unionAffiliation: "",
    company: "",
    unionName: "",
  });
  const [notifications, setNotifications] = useState({});
  const [securityControls, setSecurityControls] = useState(buildSecurityDefaults);
  const [savedInactivityThresholdDays, setSavedInactivityThresholdDays] = useState(DEFAULT_INACTIVITY_THRESHOLD_DAYS);
  const [isEditingInactivityThreshold, setIsEditingInactivityThreshold] = useState(false);
  const [inactivitySettingSaving, setInactivitySettingSaving] = useState(false);
  const [inactivitySettingError, setInactivitySettingError] = useState(null);
  const [inactivitySettingSuccess, setInactivitySettingSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminStats, setAdminStats] = useState({ total: 0, active: 0, restricted: 0 });
  const [adminError, setAdminError] = useState(null);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminModalMode, setAdminModalMode] = useState("create");
  const [adminModalSaving, setAdminModalSaving] = useState(false);
  const [adminModalError, setAdminModalError] = useState(null);
  const [adminModalFieldErrors, setAdminModalFieldErrors] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState(buildAdminFormDefaults);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [adminPasswordFocused, setAdminPasswordFocused] = useState(false);
  const [adminConfirmFocused, setAdminConfirmFocused] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openCompanyDropdownId, setOpenCompanyDropdownId] = useState(null);
  const [openUmDropdownId, setOpenUmDropdownId] = useState(null);
  const [mfaResetPendingId, setMfaResetPendingId] = useState(null);
  const [mfaResetModalOpen, setMfaResetModalOpen] = useState(false);
  const [mfaResetTargetAdmin, setMfaResetTargetAdmin] = useState(null);
  const [mfaResetReason, setMfaResetReason] = useState("User lost authenticator device");
  const [mfaResetModalError, setMfaResetModalError] = useState(null);
  const [mfaResetSuccessModalOpen, setMfaResetSuccessModalOpen] = useState(false);
  const [mfaResetSuccessMessage, setMfaResetSuccessMessage] = useState("");
  const [deptAccounts, setDeptAccounts] = useState([]);
  const [deptStats, setDeptStats] = useState({ total: 0, active: 0, restricted: 0 });
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState(null);
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [deptModalMode, setDeptModalMode] = useState("create");
  const [deptModalSaving, setDeptModalSaving] = useState(false);
  const [deptModalError, setDeptModalError] = useState(null);
  const [selectedDeptAccount, setSelectedDeptAccount] = useState(null);
  const [deptForm, setDeptForm] = useState(buildDeptFormDefaults);
  const [showDeptPassword, setShowDeptPassword] = useState(false);
  const [showDeptConfirmPassword, setShowDeptConfirmPassword] = useState(false);
  const [deptPendingId, setDeptPendingId] = useState(null);
  const [openDeptDropdownId, setOpenDeptDropdownId] = useState(null);
  const [deptMfaResetPendingId, setDeptMfaResetPendingId] = useState(null);
  const [deptMfaResetModalOpen, setDeptMfaResetModalOpen] = useState(false);
  const [deptMfaResetTarget, setDeptMfaResetTarget] = useState(null);
  const [deptMfaResetReason, setDeptMfaResetReason] = useState("User lost authenticator device");
  const [deptMfaResetModalError, setDeptMfaResetModalError] = useState(null);
  const [deptMfaResetSuccessModalOpen, setDeptMfaResetSuccessModalOpen] = useState(false);
  const [deptMfaResetSuccessMessage, setDeptMfaResetSuccessMessage] = useState("");
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    type: null,
    target: null,
    title: "",
    message: "",
    confirmLabel: "Delete",
  });
  const [deleteConfirmPending, setDeleteConfirmPending] = useState(false);
  const [deleteConfirmError, setDeleteConfirmError] = useState(null);

  // Union Management state
  const [umAffiliations, setUmAffiliations] = useState([]);
  const [umCompanies, setUmCompanies] = useState([]);
  const [umUnionNames, setUmUnionNames] = useState([]);
  const [umPositions, setUmPositions] = useState([]);
  const [umLoading, setUmLoading] = useState(false);
  const [umError, setUmError] = useState(null);
  const [umSaving, setUmSaving] = useState(false);
  // Union Management modals
  const [umModal, setUmModal] = useState({ type: null, data: null }); // type: 'addAffiliation'|'addCompany'|'addUnionName'|'addPosition'|'viewLogo'|'updateLogo'
  const [umModalForm, setUmModalForm] = useState({});
  const [umModalError, setUmModalError] = useState(null);
  const [umLogoFile, setUmLogoFile] = useState(null);
  const [umLogoPreview, setUmLogoPreview] = useState(null);
  const [umConfirm, setUmConfirm] = useState(null); // { title, message, onConfirm }
  const [umIdPreviewFlipped, setUmIdPreviewFlipped] = useState(false);

  const {
    sudoModalOpen,
    requireSudo,
    onSudoSuccess,
    onSudoCancel,
  } = useSudoMode();

  const adminSessionUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("adminUser");
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }, []);

  const currentAdminId = Number(adminSessionUser?.id ?? NaN);

  const isCurrentUserSuperAdmin = useMemo(() => {
    const rawRoles = Array.isArray(adminSessionUser?.roles) ? adminSessionUser.roles : [];
    return rawRoles
      .map((role) => {
        const value = typeof role === "object"
          ? (role.code ?? role.name ?? role.role ?? role.value)
          : role;
        return String(value ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_");
      })
      .includes("SUPER_ADMIN");
  }, [adminSessionUser]);

  const [aiAutomation, setAiAutomation] = useState(buildAiAutomationDefaults);
  const [aiModelRollouts, setAiModelRollouts] = useState(buildAiRolloutDefaults);
  const [aiMetrics, setAiMetrics] = useState(buildAiMetricsDefaults);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuccessMessage, setAiSuccessMessage] = useState(null);
  const [aiDirty, setAiDirty] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestMessage, setSmtpTestMessage] = useState(null);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [backupMessage, setBackupMessage] = useState(null);
  const [backupConfig, setBackupConfig] = useState(buildBackupManagementDefaults);
  const [backupIntegration, setBackupIntegration] = useState({ configured: false, missing: [] });
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupSaving, setBackupSaving] = useState(false);
  const [backupClearInProgress, setBackupClearInProgress] = useState(false);
  const [backupDownloadsInProgress, setBackupDownloadsInProgress] = useState({});
  const [backupSourceRef, setBackupSourceRef] = useState("Dev");
  const [backupCountdownNow, setBackupCountdownNow] = useState(() => Date.now());
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [aiSubTab, setAiSubTab] = useState('automation');
  const [confidenceHover, setConfidenceHover] = useState(false);
  const [confidenceDrag, setConfidenceDrag] = useState(false);
  const [confidencePointerValue, setConfidencePointerValue] = useState(aiAutomation?.confidenceThreshold ?? 0.85);

  const setAffiliationGradientColors = useCallback((rawStart, rawEnd) => {
    const start = normalizeHexColor(rawStart);
    const end = normalizeHexColor(rawEnd);
    const closestThemeKey = getThemeKeyFromGradient(start, end);
    setUmModalForm((prev) => ({
      ...prev,
      gradientStart: start,
      gradientEnd: end,
      colorTheme: closestThemeKey,
    }));
  }, []);

  const handleGenerateRandomGradient = useCallback(() => {
    const generated = generateVibrantGradientStops();
    setAffiliationGradientColors(generated.start, generated.end);
  }, [setAffiliationGradientColors]);

  useEffect(() => {
    setConfidencePointerValue(aiAutomation?.confidenceThreshold ?? 0.85);
  }, [aiAutomation?.confidenceThreshold]);

  useEffect(() => {
    if (activeTab !== "backup-management") {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setBackupCountdownNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [activeTab]);

  const statusOptions = useMemo(() => {
    if (adminForm?.status && !ADMIN_STATUS_OPTIONS.includes(adminForm.status)) {
      return [...ADMIN_STATUS_OPTIONS, adminForm.status];
    }
    return ADMIN_STATUS_OPTIONS;
  }, [adminForm?.status]);

  const visibleRoles = useMemo(
    () => (Array.isArray(roles) ? roles.filter((role) => !isExcludedRole(role)) : []),
    [roles],
  );

  const visibleRoleIds = useMemo(() => new Set(visibleRoles.map((role) => normalizeRoleId(role.id)).filter(Boolean)), [visibleRoles]);

  const unionOptionRows = useMemo(() => {
    const rows = [];

    const pushRow = (unionAffiliation, company, unionName) => {
      rows.push({
        unionAffiliation: normalizeUnionOptionText(unionAffiliation),
        company: normalizeUnionOptionText(company),
        unionName: normalizeUnionOptionText(unionName),
      });
    };

    flattenEmploymentOptionRows().forEach((row) => {
      pushRow(row.unionAffiliation, row.company, row.unionName);
    });

    const affiliationById = new Map(
      (Array.isArray(umAffiliations) ? umAffiliations : []).map((affiliation) => [
        Number(affiliation?.id),
        affiliation,
      ]),
    );

    const companyById = new Map();

    (Array.isArray(umCompanies) ? umCompanies : []).forEach((company) => {
      const normalizedCompanyId = Number(company?.id);
      if (Number.isFinite(normalizedCompanyId)) {
        companyById.set(normalizedCompanyId, company);
      }

      const affiliation = Number.isFinite(Number(company?.affiliationId))
        ? affiliationById.get(Number(company.affiliationId))
        : null;

      pushRow(
        company?.affiliationAcronym
          || company?.affiliationName
          || affiliation?.acronym
          || affiliation?.fullName,
        company?.name,
        "—",
      );
    });

    (Array.isArray(umAffiliations) ? umAffiliations : []).forEach((affiliation) => {
      pushRow(affiliation?.acronym || affiliation?.fullName, "—", "—");
    });

    (Array.isArray(umUnionNames) ? umUnionNames : []).forEach((unionNameEntry) => {
      const companyFromRef = Number.isFinite(Number(unionNameEntry?.companyId))
        ? companyById.get(Number(unionNameEntry.companyId))
        : null;
      const affiliationFromRef = Number.isFinite(Number(companyFromRef?.affiliationId))
        ? affiliationById.get(Number(companyFromRef.affiliationId))
        : null;

      const computedUnionName = unionNameEntry?.formattedName
        || (
          unionNameEntry?.fullName && unionNameEntry?.acronym
            ? `${unionNameEntry.fullName} (${unionNameEntry.acronym})`
            : unionNameEntry?.fullName || unionNameEntry?.acronym
        );

      pushRow(
        unionNameEntry?.affiliationAcronym
          || unionNameEntry?.affiliationName
          || companyFromRef?.affiliationAcronym
          || companyFromRef?.affiliationName
          || affiliationFromRef?.acronym
          || affiliationFromRef?.fullName,
        unionNameEntry?.companyName || companyFromRef?.name,
        computedUnionName,
      );
    });

    const deduped = new Map();
    rows.forEach((row) => {
      const key = `${row.unionAffiliation.toLowerCase()}|${row.company.toLowerCase()}|${row.unionName.toLowerCase()}`;
      if (!deduped.has(key)) {
        deduped.set(key, row);
      }
    });

    return Array.from(deduped.values()).sort((a, b) => (
      a.unionAffiliation.localeCompare(b.unionAffiliation)
      || a.company.localeCompare(b.company)
      || a.unionName.localeCompare(b.unionName)
    ));
  }, [umAffiliations, umCompanies, umUnionNames]);

  const unionCountsByOptionKey = useMemo(() => {
    const map = new Map();
    unionMembershipRows.forEach((row) => {
      const key = `${normalizeUnionOptionText(row.unionAffiliation).toLowerCase()}|${normalizeUnionOptionText(row.company).toLowerCase()}|${normalizeUnionOptionText(row.unionName).toLowerCase()}`;
      const current = map.get(key) ?? 0;
      const count = Number(row.memberCount ?? 0);
      map.set(key, current + (Number.isFinite(count) ? count : 0));
    });
    return map;
  }, [unionMembershipRows]);

  const totalActiveMembersForUnionBreakdown = useMemo(
    () => Array.from(unionCountsByOptionKey.values()).reduce((sum, count) => sum + Number(count ?? 0), 0),
    [unionCountsByOptionKey],
  );

  const unionMembershipDisplayRows = useMemo(() => {
    return unionOptionRows.map((optionRow) => {
      const key = `${normalizeUnionOptionText(optionRow.unionAffiliation).toLowerCase()}|${normalizeUnionOptionText(optionRow.company).toLowerCase()}|${normalizeUnionOptionText(optionRow.unionName).toLowerCase()}`;
      const memberCount = unionCountsByOptionKey.get(key) ?? 0;
      const percentage = totalActiveMembersForUnionBreakdown > 0
        ? (memberCount / totalActiveMembersForUnionBreakdown) * 100
        : 0;

      return {
        ...optionRow,
        memberCount,
        percentage,
      };
    });
  }, [unionOptionRows, unionCountsByOptionKey, totalActiveMembersForUnionBreakdown]);

  const unionAffiliationFilterOptions = useMemo(
    () => Array.from(new Set(
      unionOptionRows
        .map((row) => row.unionAffiliation)
        .filter((value) => value && value !== "—"),
    )).sort((a, b) => a.localeCompare(b)),
    [unionOptionRows],
  );

  const companyFilterOptions = useMemo(() => {
    const rows = unionOptionRows.filter((row) => (
      !unionFilters.unionAffiliation || row.unionAffiliation === unionFilters.unionAffiliation
    ));
    return Array.from(new Set(
      rows
        .map((row) => row.company)
        .filter((value) => value && value !== "—"),
    )).sort((a, b) => a.localeCompare(b));
  }, [unionOptionRows, unionFilters.unionAffiliation]);

  const unionNameFilterOptions = useMemo(() => {
    const rows = unionOptionRows.filter((row) => (
      (!unionFilters.unionAffiliation || row.unionAffiliation === unionFilters.unionAffiliation)
      && (!unionFilters.company || row.company === unionFilters.company)
    ));
    return Array.from(new Set(
      rows
        .map((row) => row.unionName)
        .filter((value) => value && value !== "—"),
    )).sort((a, b) => a.localeCompare(b));
  }, [unionOptionRows, unionFilters.unionAffiliation, unionFilters.company]);

  const filteredUnionMembershipRows = useMemo(
    () => unionMembershipDisplayRows.filter((row) => (
      row.memberCount > 0
      &&
      (!unionFilters.unionAffiliation || row.unionAffiliation === unionFilters.unionAffiliation)
      && (!unionFilters.company || row.company === unionFilters.company)
      && (!unionFilters.unionName || row.unionName === unionFilters.unionName)
    )),
    [unionMembershipDisplayRows, unionFilters],
  );

  const hasActiveUnionFilters = Boolean(
    unionFilters.unionAffiliation || unionFilters.company || unionFilters.unionName,
  );

  const backupRunAnalytics = useMemo(() => {
    const allRuns = Array.isArray(backupHistory) ? backupHistory : [];
    const totalRuns = allRuns.length;

    let successRuns = 0;
    let failedRuns = 0;
    let runningRuns = 0;

    allRuns.forEach((row) => {
      const status = String(row?.status ?? '').trim().toLowerCase();
      const conclusion = String(row?.conclusion ?? '').trim().toLowerCase();

      if (conclusion === 'success') {
        successRuns += 1;
        return;
      }

      if (['failure', 'failed', 'cancelled', 'timed_out', 'action_required'].includes(conclusion)) {
        failedRuns += 1;
        return;
      }

      if (['queued', 'in_progress', 'waiting', 'requested', 'pending'].includes(status)) {
        runningRuns += 1;
      }
    });

    const successRate = totalRuns > 0 ? Math.round((successRuns / totalRuns) * 100) : 0;

    const latestRuns = allRuns.slice(0, 6).map((row, index) => {
      const status = String(row?.status ?? '').trim().toLowerCase();
      const conclusion = String(row?.conclusion ?? '').trim().toLowerCase();
      let state = 'other';
      let label = 'Other';

      if (conclusion === 'success') {
        state = 'success';
        label = 'Succeeded';
      } else if (['failure', 'failed', 'cancelled', 'timed_out', 'action_required'].includes(conclusion)) {
        state = 'failed';
        label = 'Failed';
      } else if (['queued', 'in_progress', 'waiting', 'requested', 'pending'].includes(status)) {
        state = 'running';
        label = 'Running';
      }

      return {
        id: row?.id ?? `latest-${index}`,
        label,
        state,
        runLabel: row?.runNumber ? `Run #${row.runNumber}` : `Run #${row?.id ?? index + 1}`,
        createdAt: row?.createdAt ?? null,
      };
    });

    return {
      totalRuns,
      successRuns,
      failedRuns,
      runningRuns,
      successRate,
      latestRuns,
    };
  }, [backupHistory]);

  const backupScheduleSummary = useMemo(() => {
    const parseDate = (input) => {
      if (!input) {
        return null;
      }

      if (input instanceof Date) {
        return Number.isNaN(input.getTime()) ? null : input;
      }

      if (typeof input === "number") {
        const parsed = new Date(input);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      if (typeof input === "string") {
        const trimmed = input.trim();
        if (!trimmed) {
          return null;
        }
        const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      return null;
    };

    const formatCountdown = (milliseconds) => {
      if (!Number.isFinite(milliseconds)) {
        return "--";
      }
      if (milliseconds <= 0) {
        return "Due now";
      }

      const totalSeconds = Math.floor(milliseconds / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      }
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      }
      return `${minutes}m ${seconds}s`;
    };

    const formatScheduleDateTime = (value) => {
      const date = parseDate(value);
      if (!date) {
        return "—";
      }
      return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    };

    const allRuns = Array.isArray(backupHistory) ? backupHistory : [];
    const intervalRaw = Number(backupConfig?.intervalDays);
    const intervalDays = Number.isFinite(intervalRaw) && intervalRaw > 0
      ? Math.max(1, Math.floor(intervalRaw))
      : 3;
    const isEnabled = Boolean(backupConfig?.enabled);
    const lastRunEntry = allRuns.find((row) => parseDate(row?.createdAt));
    const lastRunDate = parseDate(lastRunEntry?.createdAt);
    const baseDate = lastRunDate ?? new Date(backupCountdownNow);
    const nextRunDate = new Date(baseDate.getTime() + (intervalDays * 24 * 60 * 60 * 1000));
    const remainingMs = nextRunDate.getTime() - backupCountdownNow;

    if (!isEnabled) {
      return {
        isEnabled,
        intervalDays,
        nextRunLabel: "Automation is currently disabled",
        countdownLabel: "Disabled",
        countdownTone: "is-failed",
        lastRunLabel: lastRunDate ? formatScheduleDateTime(lastRunDate) : "No backup run recorded",
      };
    }

    return {
      isEnabled,
      intervalDays,
      nextRunLabel: formatScheduleDateTime(nextRunDate),
      countdownLabel: formatCountdown(remainingMs),
      countdownTone: remainingMs <= 0 ? "is-running" : "is-success",
      lastRunLabel: lastRunDate ? formatScheduleDateTime(lastRunDate) : "Using current time as baseline",
    };
  }, [backupConfig?.enabled, backupConfig?.intervalDays, backupHistory, backupCountdownNow]);

  const updateUnionFilter = (key, value) => {
    if (key === "unionAffiliation") {
      setUnionFilters({ unionAffiliation: value, company: "", unionName: "" });
      return;
    }
    if (key === "company") {
      setUnionFilters((prev) => ({ ...prev, company: value, unionName: "" }));
      return;
    }
    setUnionFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearUnionFilters = () => {
    setUnionFilters({ unionAffiliation: "", company: "", unionName: "" });
  };

  const applyAdminPayload = (payload) => {
    const admins = payload?.admins ?? [];
    const meta = payload?.meta ?? {};
    setAdminUsers(admins);
    setAdminStats({
      total: Number(meta.total ?? admins.length ?? 0),
      active: Number(meta.active ?? 0),
      restricted: Number(meta.restricted ?? 0),
    });
  };

  const applyAiSettingsPayload = (settings, options = {}) => {
    const { resetMessage = true } = options;
    setAiAutomation(normalizeAutomationControls(settings?.automationControls));
    setAiMetrics(normalizeMetrics(settings?.metrics));
    setAiModelRollouts(normalizeRollouts(settings?.modelRollouts));
    setAiDirty(false);
    if (resetMessage) {
      setAiSuccessMessage(null);
    }
  };

  const loadAdminUsers = async () => {
    try {
      setAdminsLoading(true);
      setAdminError(null);
      const response = await client.get("/api/admin/settings/admin-users");
      applyAdminPayload(response.data ?? {});
    } catch (err) {
      setAdminError(err?.response?.data?.message ?? "Unable to load admin users");
    } finally {
      setAdminsLoading(false);
    }
  };

  const loadAiSettings = async () => {
    let succeeded = false;
    try {
      setAiLoading(true);
      setAiError(null);
      setAiSuccessMessage(null);
      const response = await client.get("/api/admin/ai/settings");
      applyAiSettingsPayload(response.data?.settings ?? {});
      succeeded = true;
    } catch (err) {
      setAiError(err?.response?.data?.message ?? "Unable to load AI settings");
      setAiAutomation(buildAiAutomationDefaults());
      setAiModelRollouts(buildAiRolloutDefaults());
      setAiMetrics(buildAiMetricsDefaults());
      setAiDirty(false);
      succeeded = false;
    } finally {
      setAiLoading(false);
    }
    return succeeded;
  };

  const openCreateAdminModal = () => {
    setSelectedAdmin(null);
    setAdminModalMode("create");
    setAdminForm(buildAdminFormDefaults());
    setAdminModalError(null);
    setAdminModalFieldErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAdminPasswordFocused(false);
    setAdminConfirmFocused(false);
    setAdminModalOpen(true);
  };

  const openEditAdminModal = (admin) => {
    setSelectedAdmin(admin);
    setAdminModalMode("edit");
    setAdminModalError(null);
    setAdminModalFieldErrors({});
    setAdminForm({
      email: admin.email ?? "",
      firstName: admin.firstName ?? "",
      lastName: admin.lastName ?? "",
      password: "",
      confirmPassword: "",
      status: admin.status ?? "active",
      roles: (Array.isArray(admin.roleIds)
        ? admin.roleIds.map((roleId) => Number(roleId)).filter((value) => Number.isFinite(value))
        : (Array.isArray(admin.roles) ? admin.roles : []).map((role) => Number(role?.id ?? role)).filter((value) => Number.isFinite(value)))
        .filter((roleId) => visibleRoleIds.has(roleId)),
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAdminPasswordFocused(false);
    setAdminConfirmFocused(false);
    setAdminModalOpen(true);
  };

  const closeAdminModal = () => {
    setAdminModalOpen(false);
    setSelectedAdmin(null);
    setAdminModalError(null);
    setAdminModalSaving(false);
    setAdminModalFieldErrors({});
    setAdminForm(buildAdminFormDefaults());
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAdminPasswordFocused(false);
    setAdminConfirmFocused(false);
  };

  const setAdminFieldError = (field, message) => {
    setAdminModalFieldErrors((prev) => ({ ...prev, [field]: message }));
  };

  const handleAdminPasswordFocus = (field) => {
    if (field === "password") {
      setAdminPasswordFocused(true);
    } else {
      setAdminConfirmFocused(true);
    }
  };

  const handleAdminPasswordBlur = (field) => {
    if (field === "password") {
      setAdminPasswordFocused(false);
    } else {
      setAdminConfirmFocused(false);
    }
  };

  const clearAdminFieldError = (field) => {
    setAdminModalFieldErrors((prev) => {
      if (!prev || !prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateAdminForm = (field, value) => {
    clearAdminFieldError(field);
    setAdminForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleRoleSelection = (roleId) => {
    const normalized = Number(roleId);
    if (!Number.isFinite(normalized)) {
      return;
    }
    setAdminForm((prev) => {
      const nextRoles = new Set(prev.roles ?? []);
      if (nextRoles.has(normalized)) {
        nextRoles.delete(normalized);
      } else {
        nextRoles.add(normalized);
      }
      return { ...prev, roles: Array.from(nextRoles) };
    });
  };

  const toggleDropdown = (adminId) => {
    setOpenDropdownId((prev) => (prev === adminId ? null : adminId));
  };

  const toggleCompanyDropdown = (companyId) => {
    setOpenCompanyDropdownId((prev) => (prev === companyId ? null : companyId));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.admin-action-menu')) {
        setOpenDropdownId(null);
      }
      if (openCompanyDropdownId && !event.target.closest('.admin-action-menu')) {
        setOpenCompanyDropdownId(null);
      }
      if (openUmDropdownId && !event.target.closest('.admin-action-menu')) {
        setOpenUmDropdownId(null);
      }
      if (openDeptDropdownId && !event.target.closest('.admin-action-menu')) {
        setOpenDeptDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId, openCompanyDropdownId, openDeptDropdownId, openUmDropdownId]);

  const mergeCompaniesWithStats = useCallback((customCompanies = [], statsCompanies = []) => {
    const statsByCode = new Map();
    const statsByName = new Map();

    statsCompanies.forEach((entry) => {
      if (!entry) {
        return;
      }
      const codeKey = String(entry.code ?? "").trim().toUpperCase();
      if (codeKey) {
        statsByCode.set(codeKey, entry);
      }
      const nameKey = String(entry.name ?? "").trim().toLowerCase();
      if (nameKey) {
        statsByName.set(nameKey, entry);
      }
    });

    const now = Date.now();

    return (Array.isArray(customCompanies) ? customCompanies : []).map((company, index) => {
      const base = company || {};
      const id = base.id ?? `org-${now}-${index}-${Math.floor(Math.random() * 10000)}`;
      const code = String(base.code ?? "").trim();
      const name = String(base.name ?? "").trim();
      const statsEntry = statsByCode.get(code.toUpperCase()) || statsByName.get(name.toLowerCase());

      const listMembers = Array.isArray(base.members) ? base.members.length : Number(base.members);
      const primaryMembers = Number(base.memberCount);
      const statsMembers = Number(statsEntry?.memberCount);

      const resolvedMemberCount = Number.isFinite(primaryMembers)
        ? primaryMembers
        : Number.isFinite(listMembers)
          ? listMembers
          : Number.isFinite(statsMembers)
            ? statsMembers
            : null;

      const resolvedActiveMembers = Number.isFinite(Number(base.activeMembers))
        ? Number(base.activeMembers)
        : Number.isFinite(Number(statsEntry?.activeMembers))
          ? Number(statsEntry?.activeMembers)
          : null;

      return {
        ...base,
        id,
        code,
        name,
        memberCount: resolvedMemberCount,
        activeMembers: resolvedActiveMembers,
        status: base.status || "Active",
      };
    });
  }, []);

  const sanitizeCompaniesForSave = useCallback((companyList = []) => (
    (Array.isArray(companyList) ? companyList : []).map(({ memberCount, activeMembers, ...rest }) => rest)
  ), []);

  const formatMemberCount = (value) => {
    if (value === null || value === undefined) {
      return "—";
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return "—";
    }
    return numeric.toLocaleString("en-US");
  };

  const formatPercentage = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return "0.00%";
    }
    return `${numeric.toFixed(2)}%`;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "—";
    }

    const coerceDate = (input) => {
      if (input instanceof Date) {
        return Number.isNaN(input.getTime()) ? null : input;
      }

      if (typeof input === "number") {
        const parsed = new Date(input);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      if (typeof input === "string") {
        const trimmed = input.trim();
        if (!trimmed) {
          return null;
        }
        const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      return null;
    };

    const date = coerceDate(value);
    if (!date) {
      return value;
    }

    return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  };

  const resolveStatusTone = (status) => {
    switch (status) {
      case "active":
        return "is-green";
      case "invited":
        return "is-blue";
      case "suspended":
        return "is-orange";
      case "disabled":
        return "is-red";
      default:
        return "";
    }
  };

  const handleAdminSubmit = async (event) => {
    event?.preventDefault();
    setAdminModalError(null);
    setAdminModalFieldErrors({});

    const trimmedEmail = adminForm.email.trim();
    const trimmedFirstName = adminForm.firstName.trim();
    const trimmedLastName = adminForm.lastName.trim();

    if (!trimmedEmail) {
      const message = "Email is required.";
      setAdminFieldError("email", message);
      setAdminModalError(message);
      return;
    }

    const missingFirstName = !trimmedFirstName;
    const missingLastName = !trimmedLastName;
    if (missingFirstName || missingLastName) {
      const message = "First name and last name are required.";
      if (missingFirstName) {
        setAdminFieldError("firstName", "First name is required.");
      }
      if (missingLastName) {
        setAdminFieldError("lastName", "Last name is required.");
      }
      setAdminModalError(message);
      return;
    }

    if (adminModalMode === "create") {
      const passwordError = validatePassword(adminForm.password);
      if (passwordError) {
        setAdminFieldError("password", passwordError);
        setAdminModalError(passwordError);
        return;
      }

      if (adminForm.password !== adminForm.confirmPassword) {
        const message = "Passwords must match.";
        setAdminFieldError("confirmPassword", message);
        setAdminModalError(message);
        return;
      }
    }

    const payload = {
      email: trimmedEmail.toLowerCase(),
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      status: adminForm.status,
      roles: (adminForm.roles ?? [])
        .map((roleId) => Number(roleId))
        .filter((value) => Number.isFinite(value) && visibleRoleIds.has(value)),
    };

    const normalizedEmail = payload.email;
    const conflictingAdmin = (adminUsers ?? []).find((existing) => {
      if (!existing?.email) {
        return false;
      }
      const existingEmail = existing.email.toLowerCase();
      if (existingEmail !== normalizedEmail) {
        return false;
      }
      if (selectedAdmin?.id && existing.id === selectedAdmin.id) {
        return false;
      }
      return true;
    });

    if (conflictingAdmin) {
      const message = "Another admin already uses that email address.";
      setAdminFieldError("email", message);
      setAdminModalError(message);
      return;
    }

    setAdminModalSaving(true);

    try {
      if (adminModalMode === "create") {
        await client.post("/api/admin/settings/admin-users", {
          ...payload,
          password: adminForm.password,
        });
      } else if (selectedAdmin?.id) {
        await client.put(`/api/admin/settings/admin-users/${selectedAdmin.id}`, payload);
      }

      await loadAdminUsers();
      closeAdminModal();
    } catch (err) {
      if (err?.response?.status === 409) {
        const message = err?.response?.data?.message ?? "Another admin already uses that email address.";
        setAdminFieldError("email", message);
      }
      setAdminModalError(
        err?.response?.data?.message
          ?? `Unable to ${adminModalMode === "create" ? "create" : "update"} admin user`,
      );
    } finally {
      setAdminModalSaving(false);
    }
  };

  const executeDeleteAdmin = async (admin) => {
    if (!admin?.id) {
      return;
    }
    try {
      setPendingActionId(admin.id);
      setAdminError(null);
      await client.delete(`/api/admin/settings/admin-users/${admin.id}`);
      await loadAdminUsers();
    } catch (err) {
      const message = err?.response?.data?.message ?? "Unable to delete admin user";
      setAdminError(message);
      throw new Error(message);
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDeleteAdmin = (admin) => {
    if (!admin?.id) {
      return;
    }

    setDeleteConfirmError(null);
    setDeleteConfirmModal({
      isOpen: true,
      type: "admin",
      target: admin,
      title: "Delete administrator account",
      message: `Delete admin ${admin.email}? This cannot be undone.`,
      confirmLabel: "Delete admin",
    });
  };

  const closeMfaResetModal = () => {
    if (mfaResetPendingId) {
      return;
    }
    setMfaResetModalOpen(false);
    setMfaResetTargetAdmin(null);
    setMfaResetReason("User lost authenticator device");
    setMfaResetModalError(null);
  };

  const executeResetAdminMfa = async (admin, reason, sudoToken) => {
    if (!admin?.id) {
      return;
    }

    try {
      setMfaResetPendingId(admin.id);
      setAdminError(null);

      await client.post(
        `/api/admin/settings/admin-users/${admin.id}/reset-mfa`,
        { reason },
        {
          headers: {
            "X-Sudo-Token": sudoToken,
          },
        },
      );

      await loadAdminUsers();
      setMfaResetModalOpen(false);
      setMfaResetTargetAdmin(null);
      setMfaResetReason("User lost authenticator device");
      setMfaResetModalError(null);
      setMfaResetSuccessMessage(`Successfully reset MFA for ${admin.email}.`);
      setMfaResetSuccessModalOpen(true);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.requiresSudo) {
        requireSudo((nextSudoToken) => {
          void executeResetAdminMfa(admin, reason, nextSudoToken);
        });
        return;
      }
      setMfaResetModalError(err?.response?.data?.message ?? "Unable to reset MFA for this admin user.");
    } finally {
      setMfaResetPendingId(null);
    }
  };

  const handleResetAdminMfa = (admin) => {
    if (!admin?.id) {
      return;
    }

    if (!isCurrentUserSuperAdmin) {
      setAdminError("Only Super Admin accounts can reset MFA for administrators.");
      return;
    }

    if (Number.isFinite(currentAdminId) && Number(admin.id) === currentAdminId) {
      setAdminError("Use your own Security Settings page to manage your MFA.");
      return;
    }

    setMfaResetTargetAdmin(admin);
    setMfaResetReason("User lost authenticator device");
    setMfaResetModalError(null);
    setMfaResetModalOpen(true);
  };

  const submitMfaReset = () => {
    if (!mfaResetTargetAdmin?.id) {
      return;
    }

    const reason = String(mfaResetReason ?? "").trim();
    if (reason.length < 10) {
      setMfaResetModalError("A reason with at least 10 characters is required.");
      return;
    }

    setMfaResetModalError(null);
    requireSudo((sudoToken) => {
      void executeResetAdminMfa(mfaResetTargetAdmin, reason, sudoToken);
    });
  };

  // ─── Department Accounts ───────────────────────────────────────────────

  const loadDeptAccounts = async () => {
    try {
      setDeptLoading(true);
      setDeptError(null);
      const response = await getDepartmentAccountsSettings();
      setDeptAccounts(response?.data?.accounts ?? []);
      const meta = response?.data?.meta ?? {};
      const accounts = response?.data?.accounts ?? [];
      setDeptStats({
        total: Number(meta.total ?? accounts.length ?? 0),
        active: Number(meta.active ?? 0),
        restricted: Number(meta.restricted ?? 0),
      });
    } catch (err) {
      setDeptError(err?.response?.data?.message ?? "Unable to load department accounts");
      setDeptStats({ total: 0, active: 0, restricted: 0 });
    } finally {
      setDeptLoading(false);
    }
  };

  const openCreateDeptModal = () => {
    setSelectedDeptAccount(null);
    setDeptModalMode("create");
    setDeptForm(buildDeptFormDefaults());
    setDeptModalError(null);
    setShowDeptPassword(false);
    setShowDeptConfirmPassword(false);
    setDeptModalOpen(true);
  };

  const openEditDeptModal = (account) => {
    setSelectedDeptAccount(account);
    setDeptModalMode("edit");
    setDeptModalError(null);
    setDeptForm({
      email: account.email ?? "",
      firstName: account.firstName ?? "",
      lastName: account.lastName ?? "",
      password: "",
      confirmPassword: "",
      department: account.department ?? "legal",
      status: account.status ?? "active",
    });
    setShowDeptPassword(false);
    setShowDeptConfirmPassword(false);
    setDeptModalOpen(true);
  };

  const closeDeptModal = () => {
    setDeptModalOpen(false);
    setSelectedDeptAccount(null);
    setDeptModalError(null);
    setDeptModalSaving(false);
    setDeptForm(buildDeptFormDefaults());
    setShowDeptPassword(false);
    setShowDeptConfirmPassword(false);
  };

  const handleDeptSubmit = async (event) => {
    event?.preventDefault();
    setDeptModalError(null);

    const trimmedEmail = deptForm.email.trim();
    const trimmedFirstName = deptForm.firstName.trim();
    const trimmedLastName = deptForm.lastName.trim();

    if (!trimmedEmail) {
      setDeptModalError("Email is required.");
      return;
    }
    if (!trimmedFirstName || !trimmedLastName) {
      setDeptModalError("First name and last name are required.");
      return;
    }
    if (deptModalMode === "create") {
      if (!deptForm.password) {
        setDeptModalError("Password is required.");
        return;
      }
      const passwordError = validatePassword(deptForm.password);
      if (passwordError) {
        setDeptModalError(passwordError);
        return;
      }
      if (!deptForm.confirmPassword) {
        setDeptModalError("Confirm password is required.");
        return;
      }
      if (deptForm.password !== deptForm.confirmPassword) {
        setDeptModalError("Password and confirm password do not match.");
        return;
      }
    }

    setDeptModalSaving(true);
    try {
      if (deptModalMode === "create") {
        await createDepartmentAccount({
          email: trimmedEmail.toLowerCase(),
          password: deptForm.password,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          department: deptForm.department,
          status: deptForm.status,
        });
      } else if (selectedDeptAccount?.id) {
        await updateDepartmentAccount(selectedDeptAccount.id, {
          email: trimmedEmail.toLowerCase(),
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          department: deptForm.department,
          status: deptForm.status,
        });
      }
      await loadDeptAccounts();
      closeDeptModal();
    } catch (err) {
      setDeptModalError(
        err?.response?.data?.message
          ?? `Unable to ${deptModalMode === "create" ? "create" : "update"} department account`,
      );
    } finally {
      setDeptModalSaving(false);
    }
  };

  const executeDeleteDept = async (account) => {
    if (!account?.id) return;
    try {
      setDeptPendingId(account.id);
      setDeptError(null);
      await deleteDepartmentAccount(account.id);
      await loadDeptAccounts();
    } catch (err) {
      const message = err?.response?.data?.message ?? "Unable to delete department account";
      setDeptError(message);
      throw new Error(message);
    } finally {
      setDeptPendingId(null);
    }
  };

  const handleDeleteDept = (account) => {
    if (!account?.id) return;

    setDeleteConfirmError(null);
    setDeleteConfirmModal({
      isOpen: true,
      type: "department",
      target: account,
      title: "Delete department account",
      message: `Delete department account ${account.email}? This cannot be undone.`,
      confirmLabel: "Delete account",
    });
  };

  const executeDeleteCompany = async (company) => {
    if (!company?.id) {
      return;
    }
    try {
      setCompanyPendingId(company.id);
      setError(null);
      await client.delete(`/api/admin/settings/organizations/custom/company/${encodeURIComponent(company.id)}`);
      const [customResp, statsResp] = await Promise.all([
        client.get('/api/admin/settings/organizations/custom'),
        client.get('/api/admin/settings/organizations'),
      ]);
      const serverCompanies = customResp?.data?.companies ?? [];
      const statsCompanies = Array.isArray(statsResp?.data?.companies) ? statsResp.data.companies : [];
      setCompanies(mergeCompaniesWithStats(serverCompanies, statsCompanies));
    } catch (err) {
      const message = err?.response?.data?.message ?? 'Unable to delete company';
      console.error('Unable to delete company', err);
      setError(message);
      throw new Error(message);
    } finally {
      setCompanyPendingId(null);
      setOpenCompanyDropdownId(null);
    }
  };

  const handleDeleteCompany = (company) => {
    if (!company?.id) {
      return;
    }

    setDeleteConfirmError(null);
    setDeleteConfirmModal({
      isOpen: true,
      type: "company",
      target: company,
      title: "Delete company",
      message: `Delete company ${company.name}? This will remove it from the organizations list.`,
      confirmLabel: "Delete company",
    });
  };

  const closeDeleteConfirmModal = () => {
    if (deleteConfirmPending) {
      return;
    }
    setDeleteConfirmModal({
      isOpen: false,
      type: null,
      target: null,
      title: "",
      message: "",
      confirmLabel: "Delete",
    });
    setDeleteConfirmError(null);
  };

  const submitDeleteConfirmation = async () => {
    if (!deleteConfirmModal?.target || deleteConfirmPending) {
      return;
    }

    setDeleteConfirmPending(true);
    setDeleteConfirmError(null);

    try {
      if (deleteConfirmModal.type === "admin") {
        await executeDeleteAdmin(deleteConfirmModal.target);
      } else if (deleteConfirmModal.type === "department") {
        await executeDeleteDept(deleteConfirmModal.target);
      } else if (deleteConfirmModal.type === "company") {
        await executeDeleteCompany(deleteConfirmModal.target);
      }
      closeDeleteConfirmModal();
    } catch (err) {
      setDeleteConfirmError(err?.message ?? "Unable to complete delete action.");
    } finally {
      setDeleteConfirmPending(false);
    }
  };

  const toggleDeptDropdown = (accountId) => {
    setOpenDeptDropdownId((prev) => (prev === accountId ? null : accountId));
  };

  const closeDeptMfaResetModal = () => {
    if (deptMfaResetPendingId) return;
    setDeptMfaResetModalOpen(false);
    setDeptMfaResetTarget(null);
    setDeptMfaResetReason("User lost authenticator device");
    setDeptMfaResetModalError(null);
  };

  const executeDeptMfaReset = async (account, reason, sudoToken) => {
    if (!account?.id) return;
    try {
      setDeptMfaResetPendingId(account.id);
      setDeptError(null);
      await resetDepartmentAccountMfa(account.id, { reason, sudoToken });
      await loadDeptAccounts();
      setDeptMfaResetModalOpen(false);
      setDeptMfaResetTarget(null);
      setDeptMfaResetReason("User lost authenticator device");
      setDeptMfaResetModalError(null);
      setDeptMfaResetSuccessMessage(`Successfully reset MFA for ${account.email}.`);
      setDeptMfaResetSuccessModalOpen(true);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.requiresSudo) {
        requireSudo((nextSudoToken) => {
          void executeDeptMfaReset(account, reason, nextSudoToken);
        });
        return;
      }
      setDeptMfaResetModalError(err?.response?.data?.message ?? "Unable to reset MFA for this department account.");
    } finally {
      setDeptMfaResetPendingId(null);
    }
  };

  const handleResetDeptMfa = (account) => {
    if (!account?.id) return;
    setDeptMfaResetTarget(account);
    setDeptMfaResetReason("User lost authenticator device");
    setDeptMfaResetModalError(null);
    setDeptMfaResetModalOpen(true);
  };

  const submitDeptMfaReset = () => {
    if (!deptMfaResetTarget?.id) return;
    const reason = String(deptMfaResetReason ?? "").trim();
    if (reason.length < 10) {
      setDeptMfaResetModalError("A reason with at least 10 characters is required.");
      return;
    }
    setDeptMfaResetModalError(null);
    requireSudo((sudoToken) => {
      void executeDeptMfaReset(deptMfaResetTarget, reason, sudoToken);
    });
  };

  useEffect(() => {
    let subscribed = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setAiLoading(true);
        setAiError(null);
        setAiSuccessMessage(null);
        const [systemResponse, rolesResponse, adminsResponse, aiResponse, orgsResponse, orgStatsResponse] = await Promise.all([
          client.get("/api/admin/settings/system"),
          client.get("/api/admin/settings/roles"),
          client.get("/api/admin/settings/admin-users"),
          client.get("/api/admin/ai/settings"),
          client.get("/api/admin/settings/organizations/custom"),
          client.get("/api/admin/settings/organizations"),
        ]);

        if (!subscribed) {
          return;
        }

        const settingsPayload = systemResponse.data?.settings ?? {};
        const normalizedSystemSettings = {
          ...settingsPayload,
          appearancePreferences: normalizeAppearancePreferences(settingsPayload?.appearancePreferences),
        };
        setSystemSettings(normalizedSystemSettings);
        const notificationPayload = normalizedSystemSettings.notificationSettings ?? {};
        setNotifications({
          dailyDigest: Boolean(notificationPayload.dailyDigest),
          aiSummary: Boolean(notificationPayload.aiSummary),
          escalationSms: Boolean(notificationPayload.escalationSms),
        });
        setSecurityControls(normalizeSecurityControls(settingsPayload.securityControls));
        setSavedInactivityThresholdDays(
          normalizeInactivityThresholdDays(settingsPayload?.securityControls?.inactivityThresholdDays),
        );
        setIsEditingInactivityThreshold(false);
        setInactivitySettingError(null);
        setInactivitySettingSuccess(null);
        setRoles((rolesResponse.data?.roles ?? []).filter((role) => !isExcludedRole(role)));
        applyAdminPayload(adminsResponse.data ?? {});
        applyAiSettingsPayload(aiResponse.data?.settings ?? {});
        // Load persisted organizations (companies / unions / positions)
        const orgPayload = orgsResponse?.data ?? {};
        const orgStatsPayload = orgStatsResponse?.data ?? {};
        const statsCompanies = Array.isArray(orgStatsPayload.companies) ? orgStatsPayload.companies : [];
        const nextUnionMembershipRows = Array.isArray(orgStatsPayload.unionBreakdown) ? orgStatsPayload.unionBreakdown : [];

        const customCompanies = Array.isArray(orgPayload.companies) ? orgPayload.companies : [];
        setCompanies(mergeCompaniesWithStats(customCompanies, statsCompanies));
        setUnionMembershipRows(nextUnionMembershipRows);
        setTotalApprovedMembersForUnions(Number(orgStatsPayload.totalApprovedMembers ?? 0));
        if (Array.isArray(orgPayload.unions) && orgPayload.unions.length) {
          setUnions(orgPayload.unions);
        }
        if (Array.isArray(orgPayload.positions) && orgPayload.positions.length) {
          setUnionPositions(orgPayload.positions);
        }
        setAdminError(null);
        setAiError(null);
        setDirty(false);
        setAiDirty(false);
      } catch (err) {
        if (!subscribed) {
          return;
        }
        setError(err?.response?.data?.message ?? "Unable to load admin settings");
        setSystemSettings({});
        setRoles([]);
        setAdminUsers([]);
        setAdminStats({ total: 0, active: 0, restricted: 0 });
        setAdminError(err?.response?.data?.message ?? "Unable to load admin users");
        setSecurityControls(buildSecurityDefaults());
        setSavedInactivityThresholdDays(DEFAULT_INACTIVITY_THRESHOLD_DAYS);
        setIsEditingInactivityThreshold(false);
        setInactivitySettingError(null);
        setInactivitySettingSuccess(null);
        setNotifications({});
        setAiError(err?.response?.data?.message ?? "Unable to load AI settings");
        setAiAutomation(buildAiAutomationDefaults());
        setAiModelRollouts(buildAiRolloutDefaults());
        setAiMetrics(buildAiMetricsDefaults());
        setUnionMembershipRows([]);
        setTotalApprovedMembersForUnions(0);
        setAiDirty(false);
      } finally {
        if (subscribed) {
          setLoading(false);
          setAiLoading(false);
          setAdminsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      subscribed = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab === "department-accounts") {
      loadDeptAccounts();
    }
    if (activeTab === "unions") {
      loadUnionManagement();
    }
  }, [activeTab]);

  const loadUnionManagement = useCallback(async () => {
    setUmLoading(true);
    setUmError(null);
    try {
      const [affRes, compRes, unRes, posRes] = await Promise.all([
        getUnionAffiliations(),
        getUnionCompanies(),
        getUnionNames(),
        getUnionPositions(),
      ]);
      setUmAffiliations(affRes.data ?? []);
      setUmCompanies(compRes.data ?? []);
      setUmUnionNames(unRes.data ?? []);
      setUmPositions(posRes.data ?? []);
    } catch (err) {
      setUmError(err?.response?.data?.message ?? 'Unable to load union management data');
    } finally {
      setUmLoading(false);
    }
  }, []);

  const closeUmModal = () => {
    setUmModal({ type: null, data: null });
    setUmModalForm({});
    setUmModalError(null);
    setUmLogoFile(null);
    setUmLogoPreview(null);
    setUmIdPreviewFlipped(false);
  };

  const closeUmConfirm = () => setUmConfirm(null);

  const handleUmLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUmLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setUmLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAddAffiliation = async () => {
    const { fullName, acronym } = umModalForm;
    setUmModalError(null);
    if (!fullName?.trim()) return setUmModalError('Full Name is required.');
    const trimAcr = (acronym ?? '').trim().toUpperCase();
    if (!trimAcr || trimAcr.length < 2 || trimAcr.length > 6 || !/^[A-Z]+$/.test(trimAcr)) return setUmModalError('Acronym must be 2-6 uppercase letters.');
    setUmSaving(true);
    try {
      const res = await addUnionAffiliation({ fullName: fullName.trim(), acronym: trimAcr, colorTheme: umModalForm.colorTheme || 'red' });
      const newAff = res.data;
      if (umLogoFile && newAff?.id) {
        const fd = new FormData();
        fd.append('logo', umLogoFile);
        const logoRes = await uploadAffiliationLogo(newAff.id, fd);
        newAff.logoPath = logoRes.data?.logoPath;
      }
      setUmAffiliations((prev) => [...prev, newAff]);
      closeUmModal();
    } catch (err) {
      setUmModalError(err?.response?.data?.message ?? 'Unable to add affiliation.');
    } finally {
      setUmSaving(false);
    }
  };

  const handleAddCompany = async () => {
    const { affiliationId, name } = umModalForm;
    setUmModalError(null);
    if (!affiliationId) return setUmModalError('Union Affiliation is required.');
    if (!name?.trim()) return setUmModalError('Company Name is required.');
    setUmSaving(true);
    try {
      const res = await addUnionCompany({ affiliationId, name: name.trim() });
      setUmCompanies((prev) => [...prev, res.data]);
      closeUmModal();
    } catch (err) {
      setUmModalError(err?.response?.data?.message ?? 'Unable to add company.');
    } finally {
      setUmSaving(false);
    }
  };

  const handleAddUnionName = async () => {
    const { companyId, fullName, acronym } = umModalForm;
    setUmModalError(null);
    if (!companyId) return setUmModalError('Company is required.');
    if (!fullName?.trim()) return setUmModalError('Full Name is required.');
    const trimAcr = (acronym ?? '').trim().toUpperCase();
    if (!trimAcr || trimAcr.length < 2 || trimAcr.length > 6 || !/^[A-Z]+$/.test(trimAcr)) return setUmModalError('Acronym must be 2-6 uppercase letters.');
    setUmSaving(true);
    try {
      const res = await addUnionName({ companyId, fullName: fullName.trim(), acronym: trimAcr });
      setUmUnionNames((prev) => [...prev, res.data]);
      closeUmModal();
    } catch (err) {
      setUmModalError(err?.response?.data?.message ?? 'Unable to add union name.');
    } finally {
      setUmSaving(false);
    }
  };

  const handleAddPosition = async () => {
    const { name } = umModalForm;
    setUmModalError(null);
    if (!name?.trim()) return setUmModalError('Position name is required.');
    setUmSaving(true);
    try {
      const res = await addUnionPosition({ name: name.trim() });
      setUmPositions((prev) => [...prev, res.data]);
      closeUmModal();
    } catch (err) {
      setUmModalError(err?.response?.data?.message ?? 'Unable to add position.');
    } finally {
      setUmSaving(false);
    }
  };

  const handleUploadLogo = async () => {
    if (!umLogoFile || !umModal.data?.id) return;
    setUmSaving(true);
    try {
      const fd = new FormData();
      fd.append('logo', umLogoFile);
      const res = await uploadAffiliationLogo(umModal.data.id, fd);
      setUmAffiliations((prev) => prev.map((a) => a.id === umModal.data.id ? { ...a, logoPath: res.data?.logoPath } : a));
      closeUmModal();
    } catch (err) {
      setUmModalError(err?.response?.data?.message ?? 'Unable to upload logo.');
    } finally {
      setUmSaving(false);
    }
  };

  const handleToggleStatus = (entityType, item) => {
    const nextStatus = item.status === 'active' ? 'disabled' : 'active';
    const action = nextStatus === 'disabled' ? 'disable' : 'enable';
    setUmConfirm({
      title: `${action === 'disable' ? 'Disable' : 'Enable'} ${entityType}`,
      message: action === 'disable'
        ? 'Disabling this option will prevent it from appearing in new member registrations. Existing members will not be affected. Continue?'
        : `Are you sure you want to enable this ${entityType.toLowerCase()}?`,
      onConfirm: async () => {
        try {
          if (entityType === 'Affiliation') {
            await updateAffiliationStatus(item.id, nextStatus);
            setUmAffiliations((prev) => prev.map((a) => a.id === item.id ? { ...a, status: nextStatus } : a));
          } else if (entityType === 'Company') {
            await updateUnionCompanyStatus(item.id, nextStatus);
            setUmCompanies((prev) => prev.map((c) => c.id === item.id ? { ...c, status: nextStatus } : c));
          } else if (entityType === 'Union Name') {
            await updateUnionNameStatus(item.id, nextStatus);
            setUmUnionNames((prev) => prev.map((u) => u.id === item.id ? { ...u, status: nextStatus } : u));
          } else if (entityType === 'Position') {
            await updatePositionStatus(item.id, nextStatus);
            setUmPositions((prev) => prev.map((p) => p.id === item.id ? { ...p, status: nextStatus } : p));
          }
          closeUmConfirm();
        } catch (err) {
          setUmError(err?.response?.data?.message ?? `Unable to ${action} ${entityType.toLowerCase()}.`);
          closeUmConfirm();
        }
      },
    });
  };

  const toggle = (key) => {
    setNotifications((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      setDirty(true);
      return next;
    });
  };

  const toggleSecurityFlag = (key) => {
    setSecurityControls((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      setDirty(true);
      return next;
    });
  };

  const updateSecurityControl = (key, value) => {
    setSecurityControls((prev) => {
      if (prev[key] === value) {
        return prev;
      }
      setDirty(true);
      return { ...prev, [key]: value };
    });
  };

  const toggleAiAutomation = (key) => {
    setAiAutomation((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const newValue = !prev[key];
      const next = { ...prev, [key]: newValue };
      setAiDirty(true);
      setAiSuccessMessage(null);

      // If Active Learning is being disabled, reset sampling coverage to 0%
      if (key === 'enableActiveLearning' && newValue === false) {
        setAiModelRollouts((prevRollouts) => {
          const nextRollouts = Array.isArray(prevRollouts) ? [...prevRollouts] : [];
          if (nextRollouts.length === 0) {
            nextRollouts[0] = { id: `model-0`, name: 'default', capabilities: [], coverage: 0 };
          } else {
            nextRollouts[0] = { ...nextRollouts[0], coverage: 0 };
          }
          return nextRollouts;
        });
      }

      return next;
    });
  };

  const handleAiPublish = async () => {
    try {
      setAiSaving(true);
      setAiError(null);
      setAiSuccessMessage(null);
      const response = await client.put("/api/admin/ai/settings", {
        settings: {
          automationControls: aiAutomation,
          modelRollouts: aiModelRollouts,
          metrics: aiMetrics,
        },
      });
      const nextSettings = response.data?.settings;
      if (nextSettings) {
        applyAiSettingsPayload(nextSettings, { resetMessage: false });
      } else {
        setAiDirty(false);
      }
      setAiSuccessMessage("AI settings published");
    } catch (err) {
      setAiError(err?.response?.data?.message ?? "Unable to publish AI settings");
    } finally {
      setAiSaving(false);
    }
  };

  const handleAiSyncModels = async () => {
    const synced = await loadAiSettings();
    if (synced) {
      setAiSuccessMessage("AI settings synced with database");
    }
  };

  const aiCoverageAverage = useMemo(() => {
    if (!aiModelRollouts.length) {
      return 0;
    }
    const total = aiModelRollouts.reduce(
      (accumulator, rollout) => accumulator + (Number(rollout.coverage ?? 0) || 0),
      0,
    );
    return total / aiModelRollouts.length;
  }, [aiModelRollouts]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      await client.put("/api/admin/settings/system", {
        settings: {
          notificationSettings: notifications,
          securityControls,
          systemSettings,
        },
      });
      setSavedInactivityThresholdDays(normalizeInactivityThresholdDays(securityControls.inactivityThresholdDays));
      setIsEditingInactivityThreshold(false);
      setInactivitySettingError(null);
      setInactivitySettingSuccess(null);
      setDirty(false);
      setSuccessMessage("Settings saved");
    } catch (err) {
      setError(err?.response?.data?.message ?? "Unable to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      setSmtpTesting(true);
      setSmtpTestMessage(null);
      const smtpPayload = systemSettings?.smtpSettings ?? {
        server: systemSettings?.smtpServer,
        port: systemSettings?.smtpPort,
        security: systemSettings?.smtpSecurity,
        username: systemSettings?.smtpUsername,
        password: systemSettings?.smtpPassword,
      };
      // Attempt server-side test endpoint; backend may implement this.
      const resp = await client.post('/api/admin/settings/system/test-smtp', { smtp: smtpPayload });
      setSmtpTestMessage(resp?.data?.message ?? 'SMTP test sent (check server logs)');
    } catch (err) {
      setSmtpTestMessage(err?.response?.data?.message ?? 'SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupInProgress(true);
      setBackupMessage(null);
      const resp = await client.post('/api/admin/settings/system/backup', {
        scope: backupConfig?.preferredScope ?? 'full',
        sourceRef: backupSourceRef,
      });
      setBackupMessage(resp?.data?.message ?? 'Backup started');
      if (activeTab === 'backup-management') {
        await loadBackupManagement();
      }
    } catch (err) {
      setBackupMessage(err?.response?.data?.message ?? 'Backup failed');
    } finally {
      setBackupInProgress(false);
    }
  };

  const loadBackupManagement = useCallback(async () => {
    try {
      setBackupLoading(true);
      const resp = await client.get('/api/admin/settings/system/backup');
      const payload = resp?.data ?? {};
      const nextConfig = payload?.config ?? buildBackupManagementDefaults();
      setBackupConfig({
        ...buildBackupManagementDefaults(),
        ...nextConfig,
        retention: {
          ...buildBackupManagementDefaults().retention,
          ...(nextConfig?.retention ?? {}),
        },
      });
      setBackupIntegration(payload?.integration ?? { configured: false, missing: [] });
      setBackupHistory(Array.isArray(payload?.history) ? payload.history : []);
      if (payload?.integration?.sourceRef) {
        setBackupSourceRef(payload.integration.sourceRef);
      }
    } catch (err) {
      setBackupMessage(err?.response?.data?.message ?? 'Unable to load backup settings');
    } finally {
      setBackupLoading(false);
    }
  }, []);

  const handleSaveBackupSettings = async () => {
    try {
      setBackupSaving(true);
      setBackupMessage(null);
      const resp = await client.put('/api/admin/settings/system/backup', { config: backupConfig });
      const saved = resp?.data?.config ?? backupConfig;
      setBackupConfig((prev) => ({
        ...prev,
        ...saved,
        retention: {
          ...prev.retention,
          ...(saved?.retention ?? {}),
        },
      }));
      setBackupMessage(resp?.data?.message ?? 'Backup settings updated');
    } catch (err) {
      setBackupMessage(err?.response?.data?.message ?? 'Unable to save backup settings');
    } finally {
      setBackupSaving(false);
    }
  };

  const handleDownloadBackupRun = async (row) => {
    const runId = Number(row?.id);
    if (!Number.isFinite(runId) || runId <= 0) {
      setBackupMessage('This backup run does not have a valid downloadable artifact reference.');
      return;
    }

    try {
      setBackupDownloadsInProgress((prev) => ({ ...prev, [runId]: true }));
      setBackupMessage(null);
      const response = await client.get(`/api/admin/settings/system/backup/download/${runId}`, {
        params: { scope: 'db-only' },
        responseType: 'blob',
      });

      const contentDisposition = String(response?.headers?.['content-disposition'] ?? '');
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] ?? `laborconnect-db-backup-${Date.now()}.zip`;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setBackupMessage(`Backup download started for run #${row?.runNumber ?? runId}.`);
    } catch (err) {
      let message = 'Unable to download this backup run';
      const responseData = err?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            message = parsed.message;
          }
        } catch (_blobError) {
          // Keep fallback message when blob payload is not JSON.
        }
      } else if (typeof responseData?.message === 'string' && responseData.message.trim()) {
        message = responseData.message;
      }

      setBackupMessage(message);
    } finally {
      setBackupDownloadsInProgress((prev) => {
        const next = { ...prev };
        delete next[runId];
        return next;
      });
    }
  };

  const handleClearBackupHistory = async () => {
    if (!backupHistory.length) {
      setBackupMessage('No backup history to clear.');
      return;
    }

    const confirmed = window.confirm('This will permanently delete recent backup run history from GitHub Actions for this workflow. Continue?');
    if (!confirmed) {
      return;
    }

    try {
      setBackupClearInProgress(true);
      setBackupMessage(null);
      const resp = await client.delete('/api/admin/settings/system/backup/history', {
        data: { limit: 50 },
      });

      setBackupMessage(resp?.data?.message ?? 'Backup history cleared.');
      await loadBackupManagement();
    } catch (err) {
      setBackupMessage(err?.response?.data?.message ?? 'Unable to clear backup history');
    } finally {
      setBackupClearInProgress(false);
    }
  };

  useEffect(() => {
    if (activeTab === "backup-management") {
      void loadBackupManagement();
    }
  }, [activeTab, loadBackupManagement]);

  const handleExportData = async () => {
    try {
      setExportInProgress(true);
      setExportMessage(null);
      const resp = await client.post('/api/admin/settings/system/export-data');
      setExportMessage(resp?.data?.message ?? 'Export started');
    } catch (err) {
      setExportMessage(err?.response?.data?.message ?? 'Export failed');
    } finally {
      setExportInProgress(false);
    }
  };

  const handleRefresh = async () => {
    setDirty(false);
    setSuccessMessage(null);
    setError(null);
    setLoading(true);
    setAiLoading(true);
    setAiError(null);
    setAiSuccessMessage(null);
    try {
      const [systemResponse, rolesResponse, adminsResponse, aiResponse, orgsResponse, orgStatsResponse] = await Promise.all([
        client.get("/api/admin/settings/system"),
        client.get("/api/admin/settings/roles"),
        client.get("/api/admin/settings/admin-users"),
        client.get("/api/admin/ai/settings"),
        client.get('/api/admin/settings/organizations/custom'),
        client.get('/api/admin/settings/organizations'),
      ]);
      const settingsPayload = systemResponse.data?.settings ?? {};
      const normalizedSystemSettings = {
        ...settingsPayload,
        appearancePreferences: normalizeAppearancePreferences(settingsPayload?.appearancePreferences),
      };
      setSystemSettings(normalizedSystemSettings);
      const notificationPayload = normalizedSystemSettings.notificationSettings ?? {};
      setNotifications({
        dailyDigest: Boolean(notificationPayload.dailyDigest),
        aiSummary: Boolean(notificationPayload.aiSummary),
        escalationSms: Boolean(notificationPayload.escalationSms),
      });
      setSecurityControls(normalizeSecurityControls(settingsPayload.securityControls));
      setSavedInactivityThresholdDays(
        normalizeInactivityThresholdDays(settingsPayload?.securityControls?.inactivityThresholdDays),
      );
      setIsEditingInactivityThreshold(false);
      setInactivitySettingError(null);
      setInactivitySettingSuccess(null);
      setRoles((rolesResponse.data?.roles ?? []).filter((role) => !isExcludedRole(role)));
      applyAdminPayload(adminsResponse.data ?? {});
      applyAiSettingsPayload(aiResponse.data?.settings ?? {});
      const orgPayload = orgsResponse?.data ?? {};
      const orgStatsPayload = orgStatsResponse?.data ?? {};
      const statsCompanies = Array.isArray(orgStatsPayload.companies) ? orgStatsPayload.companies : [];
      const nextUnionMembershipRows = Array.isArray(orgStatsPayload.unionBreakdown) ? orgStatsPayload.unionBreakdown : [];
      if (Array.isArray(orgPayload.companies) && orgPayload.companies.length) {
        setCompanies(mergeCompaniesWithStats(orgPayload.companies, statsCompanies));
      }
      setUnionMembershipRows(nextUnionMembershipRows);
      setTotalApprovedMembersForUnions(Number(orgStatsPayload.totalApprovedMembers ?? 0));
      if (Array.isArray(orgPayload.unions) && orgPayload.unions.length) {
        setUnions(orgPayload.unions);
      }
      if (Array.isArray(orgPayload.positions) && orgPayload.positions.length) {
        setUnionPositions(orgPayload.positions);
      }
      setAdminError(null);
      setAiError(null);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Unable to refresh settings");
      setAdminError(err?.response?.data?.message ?? "Unable to load admin users");
      setSecurityControls(buildSecurityDefaults());
      setSavedInactivityThresholdDays(DEFAULT_INACTIVITY_THRESHOLD_DAYS);
      setIsEditingInactivityThreshold(false);
      setInactivitySettingError(null);
      setInactivitySettingSuccess(null);
      setAiError(err?.response?.data?.message ?? "Unable to load AI settings");
      setAiAutomation(buildAiAutomationDefaults());
      setAiModelRollouts(buildAiRolloutDefaults());
      setAiMetrics(buildAiMetricsDefaults());
      setAiDirty(false);
    } finally {
      setLoading(false);
      setAiLoading(false);
    }
  };

  const tabLabelMap = {
    "user-management": "Admin Management",
    "department-accounts": "Department Management",
    unions: "Union Management",
    "password-authentication": "Password & Authentication",
    "backup-management": "Backup Management",
    configuration: "Configuration",
    companies: "Companies",
  };

  const currentTabLabel = tabLabelMap[activeTab] ?? "Admin Settings";
  const currentInactivityThresholdDays = normalizeInactivityThresholdDays(securityControls.inactivityThresholdDays);
  const isInactivityThresholdChanged = currentInactivityThresholdDays !== savedInactivityThresholdDays;

  const saveInactivityThresholdSetting = async () => {
    try {
      setInactivitySettingSaving(true);
      setInactivitySettingError(null);
      setInactivitySettingSuccess(null);

      const nextSecurityControls = {
        ...securityControls,
        inactivityThresholdDays: currentInactivityThresholdDays,
      };

      const response = await client.put("/api/admin/settings/system", {
        settings: {
          securityControls: nextSecurityControls,
        },
      });

      const returnedControls = normalizeSecurityControls(
        response?.data?.settings?.securityControls ?? nextSecurityControls,
      );

      setSecurityControls(returnedControls);
      setSavedInactivityThresholdDays(
        normalizeInactivityThresholdDays(returnedControls.inactivityThresholdDays),
      );
      setInactivitySettingSuccess("Member inactivity threshold saved.");
      return true;
    } catch (err) {
      setInactivitySettingError(err?.response?.data?.message ?? "Unable to save inactivity threshold");
      return false;
    } finally {
      setInactivitySettingSaving(false);
    }
  };

  const handleInactivityThresholdAction = async () => {
    if (!isEditingInactivityThreshold) {
      setInactivitySettingError(null);
      setInactivitySettingSuccess(null);
      setIsEditingInactivityThreshold(true);
      return;
    }

    if (!isInactivityThresholdChanged) {
      setIsEditingInactivityThreshold(false);
      return;
    }

    const saved = await saveInactivityThresholdSetting();
    if (saved) {
      setIsEditingInactivityThreshold(false);
    }
  };

  return (
    <div className="admin-settings-scope">
      <div className="admin-page admin-stack-lg admin-settings-page">
        <section className="admin-settings-hero admin-surface">
          <div className="admin-settings-hero__intro">
            <span className="admin-settings-hero__eyebrow">Control Center</span>
            <h1>Admin Settings</h1>
            <p className="admin-muted">
              Manage platform-wide defaults, escalation policies, and security controls to keep the admin experience consistent.
            </p>
          </div>
          <div className="admin-settings-hero__actions">
            {activeTab === "user-management" ? (
              <>
                <button
                  type="button"
                  className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
                  onClick={handleRefresh}
                  disabled={loading || saving}
                  aria-label="Refresh audit log"
                  title="Refresh audit log"
                >
                  <RefreshCw size={18} />
                </button>
              </>
            ) : null}
          </div>
        </section>

        {activeTab === "user-management" && error ? (
          <div className="admin-alert is-error">{error}</div>
        ) : null}
        {activeTab === "user-management" && successMessage ? (
          <div className="admin-alert is-success">{successMessage}</div>
        ) : null}
        <section className="admin-surface admin-stack-md admin-settings-workspace">
          <div className="admin-tabs">
            <div className="admin-tabs__list">
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "user-management" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("user-management")}
              >
                Admin Management
              </button>
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "department-accounts" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("department-accounts")}
              >
                Department Management
              </button>
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "unions" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("unions")}
              >
                Union Management
              </button>
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "password-authentication" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("password-authentication")}
              >
                Password &amp; Authentication
              </button>
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "backup-management" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("backup-management")}
              >
                Backup Management
              </button>
              <button
                type="button"
                className={`admin-tabs__trigger ${activeTab === "configuration" ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab("configuration")}
              >
                Configuration
              </button>
            </div>
          </div>

          {activeTab === "user-management" ? (
            <>
              <div className="admin-row">
                <div>
                  <h2>Administrator Accounts</h2>
                  <p className="admin-muted">
                    Manage system administrators, passwords, and their role assignments.
                  </p>
                </div>
                <div className="admin-row admin-row--start">
                  <button
                    type="button"
                    className="admin-button is-primary"
                    onClick={openCreateAdminModal}
                  >
                    <Plus size={16} /> Add New Admin
                  </button>
                </div>
              </div>
              <div className="admin-card-grid cols-3 admin-card-grid--stats">
                <article className="admin-card">
                  <div className="admin-card__label">Total Admins</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{adminStats.total}</div>
                    <span className="admin-chip is-blue"><Users size={18} /></span>
                  </div>
                  <div className="admin-card__meta">Includes invited and active accounts</div>
                </article>
                <article className="admin-card">
                  <div className="admin-card__label">Active</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{adminStats.active}</div>
                    <span className="admin-chip is-green"><UserCheck size={18} /></span>
                  </div>
                  <div className="admin-card__meta">Currently able to access the console</div>
                </article>
                <article className="admin-card">
                  <div className="admin-card__label">Restricted</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{adminStats.restricted}</div>
                    <span className="admin-chip is-red"><ShieldOff size={18} /></span>
                  </div>
                  <div className="admin-card__meta">Suspended or disabled accounts</div>
                </article>
              </div>
              {adminError ? <div className="admin-alert is-error">{adminError}</div> : null}
              <div className="admin-scroll">
                <table className="admin-table admin-table--condensed">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th className="admin-actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((admin, index) => {
                      const fullName = admin.fullName || [admin.firstName, admin.lastName].filter(Boolean).join(" ").trim();
                      const rolesList = Array.isArray(admin.roles) && admin.roles.length
                        ? admin.roles
                        : [];
                      const shouldOpenUp = index >= adminUsers.length - 2;
                      return (
                        <tr key={admin.id}>
                          <td>
                            <div className="admin-stack">
                              <strong>{fullName || "—"}</strong>
                            </div>
                          </td>
                          <td>{admin.email}</td>
                          <td>
                            {rolesList.length ? (
                              <div className="admin-inline-list">
                                {rolesList.map((roleName) => (
                                  <span key={roleName} className="admin-chip">{roleName}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="admin-muted">Unassigned</span>
                            )}
                          </td>
                          <td>
                            <span className={`admin-chip ${resolveStatusTone(admin.status)}`.trim()}>
                              {admin.status ?? "unknown"}
                            </span>
                          </td>
                          <td>{formatDateTime(admin.lastLoginAt)}</td>
                          <td>{formatDateTime(admin.createdAt)}</td>
                          <td>
                            <div className={`admin-table__actions admin-action-menu ${shouldOpenUp ? "is-dropup" : ""}`.trim()}>
                              <button
                                type="button"
                                className="admin-action-button admin-action-menu-trigger"
                                onClick={() => toggleDropdown(admin.id)}
                                disabled={Boolean(pendingActionId)}
                              >
                                <MoreVertical size={18} />
                              </button>
                              {openDropdownId === admin.id && (
                                <div className="admin-action-dropdown">
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item"
                                    onClick={() => {
                                      openEditAdminModal(admin);
                                      setOpenDropdownId(null);
                                    }}
                                    disabled={Boolean(pendingActionId)}
                                  >
                                    <Edit size={16} /> Edit Permissions
                                  </button>
                                  {isCurrentUserSuperAdmin ? (
                                    <button
                                      type="button"
                                      className="admin-action-dropdown-item"
                                      onClick={() => {
                                        handleResetAdminMfa(admin);
                                        setOpenDropdownId(null);
                                      }}
                                      disabled={mfaResetPendingId === admin.id || (Number.isFinite(currentAdminId) && Number(admin.id) === currentAdminId)}
                                    >
                                      {mfaResetPendingId === admin.id ? <Loader2 size={16} /> : <Shield size={16} />}
                                      Reset MFA
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item is-danger"
                                    onClick={() => {
                                      handleDeleteAdmin(admin);
                                      setOpenDropdownId(null);
                                    }}
                                    disabled={pendingActionId === admin.id}
                                  >
                                    {pendingActionId === admin.id ? <Loader2 size={16} /> : <Trash2 size={16} />}
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {adminsLoading ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="admin-empty">Loading administrators…</div>
                        </td>
                      </tr>
                    ) : null}
                    {!adminsLoading && adminUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="admin-empty">No administrator accounts found.</div>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : activeTab === 'companies' ? (
            <>
              <div className="admin-row">
                <div>
                  <h2>Registered Companies</h2>
                  <p className="admin-muted">Manage companies registered with the union.</p>
                </div>
                <div className="admin-row admin-row--start">
                  <button type="button" className="admin-button is-primary" onClick={() => {
                    setCompanyModalMode('create');
                    setCompanyForm({ id: null, name: '', code: '', description: '', industry: '', status: 'Active' });
                    setCompanyModalOpen(true);
                  }}>Add New Company</button>
                </div>
              </div>

              <div className="admin-card">
                <div className="admin-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Company Name</th>
                        <th>Code</th>
                        <th>Members</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((c, index) => {
                        const shouldOpenUp = index >= companies.length - 2;
                        return (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>{c.code}</td>
                          <td>{formatMemberCount(c.memberCount)}</td>
                          <td><span className="admin-chip is-green">{c.status}</span></td>
                          <td>
                            <div className={`admin-table__actions admin-action-menu ${shouldOpenUp ? "is-dropup" : ""}`.trim()}>
                              <button
                                type="button"
                                className="admin-action-button admin-action-menu-trigger"
                                onClick={() => toggleCompanyDropdown(c.id)}
                                disabled={Boolean(companyPendingId)}
                              >
                                <MoreVertical size={18} />
                              </button>
                              {openCompanyDropdownId === c.id && (
                                <div className="admin-action-dropdown">
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item"
                                    onClick={() => {
                                      setCompanyModalMode('edit');
                                      setCompanyForm({ id: c.id, name: c.name, code: c.code, description: c.description || '', industry: c.industry || '', status: c.status || 'Active' });
                                      setCompanyModalOpen(true);
                                      setOpenCompanyDropdownId(null);
                                    }}
                                    disabled={Boolean(companyPendingId)}
                                  >
                                    <Edit size={16} /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item"
                                    onClick={() => {
                                      navigate(`/admin/members?company=${encodeURIComponent(c.code || c.name)}`);
                                      setOpenCompanyDropdownId(null);
                                    }}
                                    disabled={Boolean(companyPendingId)}
                                  >
                                    <Eye size={16} /> View Members
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item is-danger"
                                    onClick={() => {
                                      handleDeleteCompany(c);
                                      setOpenCompanyDropdownId(null);
                                    }}
                                    disabled={companyPendingId != null && String(companyPendingId) === String(c.id)}
                                  >
                                    {companyPendingId != null && String(companyPendingId) === String(c.id) ? <Loader2 size={16} /> : <Trash2 size={16} />}
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Company modal is handled separately; Add form removed per request */}
              {companyModalOpen && (
                <div className="admin-dialog" role="dialog" aria-modal="true">
                  <div
                    className="admin-dialog__backdrop"
                    onClick={() => (companyPendingId ? undefined : setCompanyModalOpen(false))}
                    role="presentation"
                  />
                  <div className="admin-dialog__panel">
                    <div className="admin-dialog__header">
                      <h3>{companyModalMode === 'create' ? 'Add New Company' : `Edit Company: ${companyForm.name}`}</h3>
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => setCompanyModalOpen(false)}
                        disabled={companyPendingId}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="admin-dialog__body">
                      <div className="admin-form-section">
                        <div className="admin-field">
                          <span>Company Name</span>
                          <input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} required />
                        </div>
                        <div className="admin-field">
                          <span>Company Code</span>
                          <input value={companyForm.code} onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value })} required />
                        </div>
                        <div className="admin-field">
                          <span>Description</span>
                          <textarea value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} />
                        </div>
                        <div className="admin-field">
                          <span>Industry</span>
                          <input value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })} />
                        </div>
                        <div className="admin-field">
                          <span>Status</span>
                          <select value={companyForm.status} onChange={(e) => setCompanyForm({ ...companyForm, status: e.target.value })}>
                            <option>Active</option>
                            <option>Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="admin-dialog__footer">
                      <button type="button" className="admin-button" onClick={() => setCompanyModalOpen(false)} disabled={companyPendingId}>Cancel</button>
                      <button
                        type="button"
                        className="admin-button is-primary"
                        onClick={async () => {
                          if (!companyForm.name || !companyForm.code) return;
                          // use a temporary id for newly created companies so we don't collide
                          const tempId = companyModalMode === 'create' ? `tmp-${Date.now()}` : companyForm.id;
                          try {
                            setCompanyPendingId(tempId);
                            let next;
                            if (companyModalMode === 'create') {
                              next = [...companies, { id: tempId, ...companyForm }];
                            } else {
                              next = companies.map((c) => (String(c.id) === String(companyForm.id) ? { ...c, ...companyForm } : c));
                            }
                            const payloadCompanies = sanitizeCompaniesForSave(next);
                            await client.put('/api/admin/settings/organizations/custom', { companies: payloadCompanies, unions, positions: unionPositions });

                            // re-fetch from server to get persisted result
                            const [customResp, statsResp] = await Promise.all([
                              client.get('/api/admin/settings/organizations/custom'),
                              client.get('/api/admin/settings/organizations'),
                            ]);
                            const serverCompanies = customResp?.data?.companies ?? [];
                            const statsCompanies = Array.isArray(statsResp?.data?.companies) ? statsResp.data.companies : [];
                            setCompanies(mergeCompaniesWithStats(serverCompanies, statsCompanies));

                            setCompanyModalOpen(false);
                          } catch (err) {
                            console.error('Unable to save company', err);
                            setError(err?.response?.data?.message ?? 'Unable to save company');
                          } finally {
                            setCompanyPendingId(null);
                          }
                        }}
                      >
                        {companyPendingId ? 'Saving…' : (companyModalMode === 'create' ? 'Add Company' : 'Save Changes')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : activeTab === 'unions' ? (
            <>
              {umError && <div className="admin-alert is-error">{umError}</div>}
              {umLoading ? <div className="admin-empty">Loading union management data…</div> : (
                <>
                  {/* ── Union Affiliation ── */}
                  <div className="admin-row">
                    <div>
                      <h2>Union Affiliations</h2>
                      <p className="admin-muted">Manage union affiliations and their logos.</p>
                    </div>
                    <button type="button" className="admin-button is-primary" onClick={() => {
                      const defaultStops = extractThemeStops(COLOR_THEMES.red);
                      setUmModal({ type: 'addAffiliation', data: null });
                      setUmModalForm({
                        fullName: '',
                        acronym: '',
                        colorTheme: 'red',
                        gradientStart: defaultStops.start,
                        gradientEnd: defaultStops.end,
                      });
                    }}><Plus size={16} /> Add Affiliation</button>
                  </div>
                  <div className="admin-card">
                    <div className="admin-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Acronym</th><th>Full Name</th><th>Logo</th><th>Color</th><th>Status</th><th>Actions</th></tr></thead>
                        <tbody>
                          {umAffiliations.map((aff) => (
                            <tr key={aff.id}>
                              <td><strong>{aff.acronym}</strong></td>
                              <td>{aff.fullName}</td>
                              <td>
                                {aff.logoPath ? (
                                  <img src={resolveBackendAssetUrl(aff.logoPath)} alt={aff.acronym} className="um-logo-thumb" />
                                ) : <span className="admin-muted">No logo</span>}
                              </td>
                              <td>
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: COLOR_THEMES[aff.colorTheme]?.headerGradient || COLOR_THEMES.red.headerGradient, border: '2px solid #e5e7eb', display: 'inline-block' }} title={aff.colorTheme || 'red'} />
                              </td>
                              <td><span className={`admin-chip ${aff.status === 'active' ? 'is-green' : 'is-red'}`}>{aff.status}</span></td>
                              <td>
                                <div className="admin-table__actions admin-action-menu">
                                  <button type="button" className="admin-action-button admin-action-menu-trigger" onClick={() => setOpenUmDropdownId((prev) => prev === `aff-${aff.id}` ? null : `aff-${aff.id}`)}>
                                    <MoreVertical size={18} />
                                  </button>
                                  {openUmDropdownId === `aff-${aff.id}` && (
                                    <div className="admin-action-dropdown">
                                      {aff.logoPath && (
                                        <button type="button" className="admin-action-dropdown-item" onClick={() => { setUmModal({ type: 'viewLogo', data: aff }); setOpenUmDropdownId(null); }}>
                                          <Eye size={16} /> View Logo
                                        </button>
                                      )}
                                      <button type="button" className="admin-action-dropdown-item" onClick={() => { setUmModal({ type: 'updateLogo', data: aff }); setUmLogoFile(null); setUmLogoPreview(null); setOpenUmDropdownId(null); }}>
                                        <Upload size={16} /> Update Logo
                                      </button>
                                      <button type="button" className="admin-action-dropdown-item" onClick={() => { setUmModal({ type: 'changeColor', data: aff }); setUmModalForm({ colorTheme: aff.colorTheme || 'red' }); setUmModalError(null); setOpenUmDropdownId(null); }}>
                                        <Palette size={16} /> Change Color
                                      </button>
                                      <button type="button" className={`admin-action-dropdown-item${aff.status === 'active' ? ' is-danger' : ''}`} onClick={() => { handleToggleStatus('Affiliation', aff); setOpenUmDropdownId(null); }}>
                                        {aff.status === 'active' ? <><EyeOff size={16} /> Disable</> : <><Eye size={16} /> Enable</>}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!umAffiliations.length && <tr><td colSpan={6}><div className="admin-empty">No affiliations found.</div></td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Companies ── */}
                  <div className="admin-row" style={{ marginTop: '2rem' }}>
                    <div>
                      <h2>Companies</h2>
                      <p className="admin-muted">Companies belong to a union affiliation.</p>
                    </div>
                    <button type="button" className="admin-button is-primary" onClick={() => {
                      setUmModal({ type: 'addCompany', data: null });
                      setUmModalForm({ affiliationId: '', name: '' });
                    }}><Plus size={16} /> Add Company</button>
                  </div>
                  <div className="admin-card">
                    <div className="admin-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Company Name</th><th>Union Affiliation</th><th>Status</th><th>Actions</th></tr></thead>
                        <tbody>
                          {umCompanies.map((comp) => (
                            <tr key={comp.id}>
                              <td>{comp.name}</td>
                              <td>{comp.affiliationAcronym || comp.affiliationName || '—'}</td>
                              <td><span className={`admin-chip ${comp.status === 'active' ? 'is-green' : 'is-red'}`}>{comp.status}</span></td>
                              <td>
                                <div className="admin-table__actions admin-action-menu">
                                  <button type="button" className="admin-action-button admin-action-menu-trigger" onClick={() => setOpenUmDropdownId((prev) => prev === `comp-${comp.id}` ? null : `comp-${comp.id}`)}>
                                    <MoreVertical size={18} />
                                  </button>
                                  {openUmDropdownId === `comp-${comp.id}` && (
                                    <div className="admin-action-dropdown">
                                      <button type="button" className={`admin-action-dropdown-item${comp.status === 'active' ? ' is-danger' : ''}`} onClick={() => { handleToggleStatus('Company', comp); setOpenUmDropdownId(null); }}>
                                        {comp.status === 'active' ? <><EyeOff size={16} /> Disable</> : <><Eye size={16} /> Enable</>}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!umCompanies.length && <tr><td colSpan={4}><div className="admin-empty">No companies found.</div></td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Union Names ── */}
                  <div className="admin-row" style={{ marginTop: '2rem' }}>
                    <div>
                      <h2>Union Names</h2>
                      <p className="admin-muted">Union names belong to a company. Stored as "Full Name (Acronym)".</p>
                    </div>
                    <button type="button" className="admin-button is-primary" onClick={() => {
                      setUmModal({ type: 'addUnionName', data: null });
                      setUmModalForm({ companyId: '', fullName: '', acronym: '' });
                    }}><Plus size={16} /> Add Union Name</button>
                  </div>
                  <div className="admin-card">
                    <div className="admin-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Union Name</th><th>Company</th><th>Status</th><th>Actions</th></tr></thead>
                        <tbody>
                          {umUnionNames.map((un) => (
                            <tr key={un.id}>
                              <td>{un.formattedName || `${un.fullName} (${un.acronym})`}</td>
                              <td>{un.companyName || '—'}</td>
                              <td><span className={`admin-chip ${un.status === 'active' ? 'is-green' : 'is-red'}`}>{un.status}</span></td>
                              <td>
                                <div className="admin-table__actions admin-action-menu">
                                  <button type="button" className="admin-action-button admin-action-menu-trigger" onClick={() => setOpenUmDropdownId((prev) => prev === `un-${un.id}` ? null : `un-${un.id}`)}>
                                    <MoreVertical size={18} />
                                  </button>
                                  {openUmDropdownId === `un-${un.id}` && (
                                    <div className="admin-action-dropdown">
                                      <button type="button" className={`admin-action-dropdown-item${un.status === 'active' ? ' is-danger' : ''}`} onClick={() => { handleToggleStatus('Union Name', un); setOpenUmDropdownId(null); }}>
                                        {un.status === 'active' ? <><EyeOff size={16} /> Disable</> : <><Eye size={16} /> Enable</>}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!umUnionNames.length && <tr><td colSpan={4}><div className="admin-empty">No union names found.</div></td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Union Positions ── */}
                  <div className="admin-row" style={{ marginTop: '2rem' }}>
                    <div>
                      <h2>Union Positions</h2>
                      <p className="admin-muted">Manage positions available in the registration form.</p>
                    </div>
                    <button type="button" className="admin-button is-primary" onClick={() => {
                      setUmModal({ type: 'addPosition', data: null });
                      setUmModalForm({ name: '' });
                    }}><Plus size={16} /> Add Position</button>
                  </div>
                  <div className="admin-card">
                    <div className="admin-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Position Name</th><th>Status</th><th>Actions</th></tr></thead>
                        <tbody>
                          {umPositions.map((pos) => (
                            <tr key={pos.id}>
                              <td>{pos.name}</td>
                              <td><span className={`admin-chip ${pos.status === 'active' ? 'is-green' : 'is-red'}`}>{pos.status}</span></td>
                              <td>
                                <div className="admin-table__actions admin-action-menu">
                                  <button type="button" className="admin-action-button admin-action-menu-trigger" onClick={() => setOpenUmDropdownId((prev) => prev === `pos-${pos.id}` ? null : `pos-${pos.id}`)}>
                                    <MoreVertical size={18} />
                                  </button>
                                  {openUmDropdownId === `pos-${pos.id}` && (
                                    <div className="admin-action-dropdown">
                                      <button type="button" className={`admin-action-dropdown-item${pos.status === 'active' ? ' is-danger' : ''}`} onClick={() => { handleToggleStatus('Position', pos); setOpenUmDropdownId(null); }}>
                                        {pos.status === 'active' ? <><EyeOff size={16} /> Disable</> : <><Eye size={16} /> Enable</>}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!umPositions.length && <tr><td colSpan={3}><div className="admin-empty">No positions found.</div></td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Membership Breakdown (existing, preserved) ── */}
                  <div className="admin-row" style={{ marginTop: '2rem' }}>
                    <div>
                      <h2>Union Membership Breakdown</h2>
                      <p className="admin-muted">Data source: approved member employment records used by membership submissions.</p>
                    </div>
                  </div>
                  <div className="admin-card admin-filters-panel">
                    <div className="admin-filters-panel__header">
                      <div>
                        <p className="admin-card__label">Refine results</p>
                        <p className="admin-filters-panel__hint">Filter by employment dropdown fields to narrow the unions table.</p>
                      </div>
                      <div className="admin-filters-panel__actions">
                        <button type="button" className="admin-button admin-button--secondary" onClick={clearUnionFilters} disabled={!hasActiveUnionFilters}>Clear filters</button>
                      </div>
                    </div>
                    <div className="admin-filters-panel__grid">
                      <label className="admin-field">
                        <span className="admin-card__label">Union Affiliation</span>
                        <select className="admin-select" value={unionFilters.unionAffiliation} onChange={(e) => updateUnionFilter("unionAffiliation", e.target.value)}>
                          <option value="">All</option>
                          {unionAffiliationFilterOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </label>
                      <label className="admin-field">
                        <span className="admin-card__label">Company</span>
                        <select className="admin-select" value={unionFilters.company} onChange={(e) => updateUnionFilter("company", e.target.value)}>
                          <option value="">All</option>
                          {companyFilterOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </label>
                      <label className="admin-field">
                        <span className="admin-card__label">Union Name</span>
                        <select className="admin-select" value={unionFilters.unionName} onChange={(e) => updateUnionFilter("unionName", e.target.value)}>
                          <option value="">All</option>
                          {unionNameFilterOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="admin-card">
                    <div className="admin-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Union Affiliation</th><th>Company</th><th>Union Name</th><th>Member (count)</th><th>Percentage</th></tr></thead>
                        <tbody>
                          {filteredUnionMembershipRows.map((row, index) => (
                            <tr key={`${row.unionAffiliation}-${row.company}-${row.unionName}-${index}`}>
                              <td>{row.unionAffiliation || "—"}</td>
                              <td>{row.company || "—"}</td>
                              <td>{row.unionName || "—"}</td>
                              <td>{formatMemberCount(row.memberCount)}</td>
                              <td>{formatPercentage(row.percentage)}</td>
                            </tr>
                          ))}
                          {!filteredUnionMembershipRows.length && (
                            <tr><td colSpan={5}><div className="admin-empty">No rows match the selected filters.</div></td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p className="admin-muted">Percentage denominator: total active members ({formatMemberCount(totalActiveMembersForUnionBreakdown)}).</p>
                </>
              )}

            </>
          ) : activeTab === "password-authentication" ? (
            <PasswordAuthenticationSettings />
          ) : activeTab === "department-accounts" ? (
            <>
              <div className="admin-row">
                <div>
                  <h2>Department Accounts</h2>
                  <p className="admin-muted">
                    Manage department user accounts and their access.
                  </p>
                </div>
                <div className="admin-row admin-row--start">
                  <button
                    type="button"
                    className="admin-button is-primary"
                    onClick={openCreateDeptModal}
                  >
                    <Plus size={16} /> Create Department Account
                  </button>
                </div>
              </div>
              <div className="admin-card-grid cols-3 admin-card-grid--stats">
                <article className="admin-card">
                  <div className="admin-card__label">Total Accounts</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{deptStats.total}</div>
                    <span className="admin-chip is-blue"><Users size={18} /></span>
                  </div>
                  <div className="admin-card__meta">All department user accounts</div>
                </article>
                <article className="admin-card">
                  <div className="admin-card__label">Active</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{deptStats.active}</div>
                    <span className="admin-chip is-green"><UserCheck size={18} /></span>
                  </div>
                  <div className="admin-card__meta">Currently able to access department portal</div>
                </article>
                <article className="admin-card">
                  <div className="admin-card__label">Restricted</div>
                  <div className="admin-row">
                    <div className="admin-card__value">{deptStats.restricted}</div>
                    <span className="admin-chip is-red"><ShieldOff size={18} /></span>
                  </div>
                  <div className="admin-card__meta">Suspended or disabled department accounts</div>
                </article>
              </div>
              {deptError ? <div className="admin-alert is-error">{deptError}</div> : null}
              <div className="admin-scroll">
                <table className="admin-table admin-table--condensed">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th className="admin-actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptAccounts.map((account, index) => {
                      const fullName = account.fullName || [account.firstName, account.lastName].filter(Boolean).join(" ").trim();
                      const shouldOpenUp = index >= deptAccounts.length - 2;
                      return (
                        <tr key={account.id}>
                          <td>
                            <div className="admin-stack">
                              <strong>{fullName || "\u2014"}</strong>
                            </div>
                          </td>
                          <td>{account.email}</td>
                          <td>
                            <span className="admin-chip admin-chip--department">
                              {toTitleCase(account.department ?? "\u2014")}
                            </span>
                          </td>
                          <td>
                            <span className={`admin-chip ${resolveStatusTone(account.status)}`.trim()}>
                              {account.status ?? "unknown"}
                            </span>
                          </td>
                          <td>{formatDateTime(account.lastLoginAt)}</td>
                          <td>{formatDateTime(account.createdAt)}</td>
                          <td>
                            <div className={`admin-table__actions admin-action-menu ${shouldOpenUp ? "is-dropup" : ""}`.trim()}>
                              <button
                                type="button"
                                className="admin-action-button admin-action-menu-trigger"
                                onClick={() => toggleDeptDropdown(account.id)}
                                disabled={Boolean(deptPendingId)}
                              >
                                <MoreVertical size={18} />
                              </button>
                              {openDeptDropdownId === account.id && (
                                <div className="admin-action-dropdown">
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item"
                                    onClick={() => {
                                      openEditDeptModal(account);
                                      setOpenDeptDropdownId(null);
                                    }}
                                    disabled={Boolean(deptPendingId)}
                                  >
                                    <Edit size={16} /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item"
                                    onClick={() => {
                                      handleResetDeptMfa(account);
                                      setOpenDeptDropdownId(null);
                                    }}
                                    disabled={Boolean(deptMfaResetPendingId)}
                                  >
                                    {deptMfaResetPendingId === account.id ? <Loader2 size={16} /> : <Shield size={16} />}
                                    Reset MFA
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-action-dropdown-item is-danger"
                                    onClick={() => {
                                      handleDeleteDept(account);
                                      setOpenDeptDropdownId(null);
                                    }}
                                    disabled={deptPendingId === account.id}
                                  >
                                    {deptPendingId === account.id ? <Loader2 size={16} /> : <Trash2 size={16} />}
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {deptLoading ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="admin-empty">Loading department accounts…</div>
                        </td>
                      </tr>
                    ) : null}
                    {!deptLoading && deptAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="admin-empty">No department accounts found.</div>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : activeTab === "backup-management" ? (
            <section className="admin-backup-hub">
              <div className="admin-row">
                <div>
                  <h2>Backup Management</h2>
                  <p className="admin-muted">Run backups manually, control schedule behavior, and review workflow history.</p>
                </div>
                <div className="admin-row admin-row--start">
                  <button
                    type="button"
                    className="admin-button"
                    onClick={() => void loadBackupManagement()}
                    disabled={backupLoading || backupInProgress}
                  >
                    {backupLoading ? <Loader2 size={16} /> : <RefreshCw size={16} />} Refresh
                  </button>
                </div>
              </div>

              <div className="admin-card admin-backup-hero">
                <div className="admin-backup-hero__head">
                  <div>
                    <h3 className="admin-backup-hero__title">Backup Operations Console</h3>
                    <p className="admin-muted">Quick health summary of your latest backup operations.</p>
                  </div>
                </div>

                <div className="admin-backup-top-metrics">
                  <article className="admin-backup-top-card">
                    <div>
                      <small>Compliance</small>
                      <strong>{Math.round(backupRunAnalytics.successRate)}%</strong>
                      <p>Success ratio</p>
                    </div>
                  </article>
                  <article className="admin-backup-top-card">
                    <div>
                      <small>In Progress</small>
                      <strong>{backupRunAnalytics.runningRuns}</strong>
                      <p>Active runs</p>
                    </div>
                  </article>
                  <article className="admin-backup-top-card">
                    <div>
                      <small>Failed</small>
                      <strong>{backupRunAnalytics.failedRuns}</strong>
                      <p>Needs attention</p>
                    </div>
                  </article>
                </div>
              </div>

              {backupMessage ? <div className="admin-alert is-success">{backupMessage}</div> : null}

              {!isCurrentUserSuperAdmin ? (
                <div className="admin-alert is-error">Only super administrators can access backup controls.</div>
              ) : null}

              <div className="admin-backup-overview">
                <div className="admin-card admin-backup-controls-card">
                  <h3 className="admin-backup-card-title">
                    <RotateCw size={17} />
                    Backup Controls
                  </h3>
                  <div className="admin-backup-controls-grid">
                    <label className="admin-setting-item">
                      <div>
                        <strong>Enable Backup Automation</strong>
                        <p className="admin-muted">Saved in existing system settings without adding new tables.</p>
                      </div>
                      <button
                        type="button"
                        className={`admin-toggle ${backupConfig?.enabled ? "active" : ""}`.trim()}
                        onClick={() => setBackupConfig((prev) => ({ ...prev, enabled: !prev?.enabled }))}
                        disabled={!isCurrentUserSuperAdmin}
                      >
                        <span />
                      </button>
                    </label>

                    <div className="admin-field">
                      <span>Preferred Scope</span>
                      <select
                        className="admin-small-input"
                        value={backupConfig?.preferredScope ?? "full"}
                        onChange={(e) => setBackupConfig((prev) => ({ ...prev, preferredScope: e.target.value }))}
                        disabled={!isCurrentUserSuperAdmin}
                      >
                        <option value="full">Full</option>
                        <option value="db-only">DB Only</option>
                      </select>
                    </div>

                    <div className="admin-field">
                      <span>Interval (days)</span>
                      <input
                        type="number"
                        className="admin-small-input"
                        min={1}
                        max={30}
                        value={backupConfig?.intervalDays ?? 3}
                        onChange={(e) => {
                          const parsed = Number(e.target.value);
                          setBackupConfig((prev) => ({
                            ...prev,
                            intervalDays: Number.isFinite(parsed) ? parsed : 3,
                          }));
                        }}
                        disabled={!isCurrentUserSuperAdmin}
                      />
                    </div>

                    <div className="admin-field">
                      <span>Source Branch</span>
                      <input
                        value={backupSourceRef}
                        onChange={(e) => setBackupSourceRef(e.target.value)}
                        disabled={!isCurrentUserSuperAdmin}
                      />
                    </div>

                    <div className="admin-backup-actions">
                      <button
                        type="button"
                        className="admin-button is-primary"
                        onClick={handleSaveBackupSettings}
                        disabled={!isCurrentUserSuperAdmin || backupSaving}
                      >
                        {backupSaving ? "Saving..." : "Save Backup Settings"}
                      </button>
                      <button
                        type="button"
                        className="admin-button"
                        onClick={handleCreateBackup}
                        disabled={!isCurrentUserSuperAdmin || backupInProgress}
                      >
                        {backupInProgress ? "Starting..." : "Run Backup Now"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="admin-card admin-backup-integration-card">
                  <h3 className="admin-backup-card-title">
                    <ShieldCheck size={17} />
                    Integration Status
                  </h3>
                  <div className="admin-backup-meta-list">
                    <div className="admin-backup-meta-row">
                      <span className="admin-muted">Configured</span>
                      <span className={`admin-chip ${backupIntegration?.configured ? "is-green" : ""}`.trim()}>
                        {backupIntegration?.configured ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="admin-backup-meta-row">
                      <span className="admin-muted">Repository</span>
                      <span className="admin-backup-meta-value">{backupIntegration?.repo ?? "-"}</span>
                    </div>
                    <div className="admin-backup-meta-row">
                      <span className="admin-muted">Workflow</span>
                      <span className="admin-backup-meta-value">{backupIntegration?.workflow ?? "-"}</span>
                    </div>
                    {!backupIntegration?.configured && Array.isArray(backupIntegration?.missing) && backupIntegration.missing.length ? (
                      <div className="admin-alert is-error admin-backup-meta-alert">
                        Missing env: {backupIntegration.missing.join(", ")}
                      </div>
                    ) : null}
                    {backupIntegration?.historyError ? (
                      <div className="admin-alert is-error admin-backup-meta-alert">
                        {backupIntegration.historyError}
                      </div>
                    ) : null}

                    <div className="admin-backup-schedule-card" aria-live="polite">
                      <div className="admin-backup-schedule-head">
                        <Clock3 size={15} />
                        <div>
                          <strong>Next Scheduled Run</strong>
                          <small>Live countdown based on current interval.</small>
                        </div>
                      </div>

                      <p className="admin-backup-schedule-time">{backupScheduleSummary.nextRunLabel}</p>

                      <div className="admin-backup-schedule-meta">
                        <span className={`admin-backup-status-pill ${backupScheduleSummary.countdownTone}`.trim()}>
                          {backupScheduleSummary.countdownLabel}
                        </span>
                        <span className="admin-backup-schedule-note">
                          Every {backupScheduleSummary.intervalDays} day{backupScheduleSummary.intervalDays === 1 ? "" : "s"}
                        </span>
                      </div>

                      <p className="admin-backup-schedule-footnote">
                        Last run reference: {backupScheduleSummary.lastRunLabel}
                      </p>
                    </div>
                  </div>

                </div>
              </div>

              <div className="admin-card admin-backup-history-card">
                <div className="admin-row admin-row--start admin-backup-history-head">
                  <h3>Recent Backup Runs</h3>
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleClearBackupHistory}
                    disabled={!isCurrentUserSuperAdmin || backupClearInProgress || backupInProgress || !backupHistory.length}
                    title={!backupHistory.length ? 'No runs to clear' : 'Delete recent run history'}
                  >
                    {backupClearInProgress ? 'Clearing...' : 'Clear History'}
                  </button>
                </div>
                <div className="admin-scroll">
                  <table className="admin-table admin-table--condensed">
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Status</th>
                        <th>Conclusion</th>
                        <th>Event</th>
                        <th>Branch</th>
                        <th>Created</th>
                        <th>Download</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupHistory.map((row, index) => {
                        const normalizedStatus = String(row?.status ?? "").trim().toLowerCase();
                        const normalizedConclusion = String(row?.conclusion ?? "").trim().toLowerCase();
                        const isRunningStatus = ["queued", "in_progress", "waiting", "requested", "pending"].includes(normalizedStatus);
                        const showLoadingStatus = isRunningStatus || (!normalizedStatus && !normalizedConclusion && backupInProgress && index === 0);
                        const rowTone = normalizedConclusion === "success"
                          ? "success"
                          : ["failure", "failed", "cancelled", "timed_out", "action_required"].includes(normalizedConclusion)
                            ? "danger"
                            : isRunningStatus
                              ? "running"
                              : "neutral";

                        const runId = Number(row?.id);
                        const hasRunId = Number.isFinite(runId) && runId > 0;
                        const downloadable = normalizedConclusion === 'success' && hasRunId;
                        const isDownloading = hasRunId && Boolean(backupDownloadsInProgress[runId]);

                        return (
                        <tr key={row.id}>
                          <td>#{row.runNumber ?? row.id}</td>
                          <td>
                            {showLoadingStatus ? (
                              <span className="admin-backup-status is-loading">
                                <Loader2 size={14} className="admin-backup-status__spinner" />
                                {row.status ? toTitleCase(row.status) : "Backing up..."}
                              </span>
                            ) : (
                              <span className={`admin-backup-table-pill is-${rowTone}`.trim()}>{row.status ?? "-"}</span>
                            )}
                          </td>
                          <td><span className={`admin-backup-table-pill is-${rowTone}`.trim()}>{row.conclusion ?? "-"}</span></td>
                          <td>{row.event ?? "-"}</td>
                          <td>{row.headBranch ?? "-"}</td>
                          <td>{row.createdAt ? formatDateTime(row.createdAt) : "-"}</td>
                          <td>
                            <button
                              type="button"
                              className="admin-button admin-button--sm"
                              onClick={() => handleDownloadBackupRun(row)}
                              disabled={!isCurrentUserSuperAdmin || !downloadable || isDownloading}
                              title={!downloadable ? 'Download is available only for successful runs with artifacts.' : undefined}
                            >
                              <Download size={13} /> {isDownloading ? 'Downloading...' : 'Download'}
                            </button>
                          </td>
                          <td>
                            {row.htmlUrl ? (
                              <a href={row.htmlUrl} target="_blank" rel="noreferrer">Open</a>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        );
                      })}
                      {!backupHistory.length ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="admin-empty">No backup run history available yet.</div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ) : activeTab === '__removed-system-config__' ? (
            <>
              <section className="admin-surface admin-stack-md">
                <header className="admin-row">
                  <div className="admin-heading-inline">
                    <Gauge size={18} />
                    <div>
                      <h2>System Config</h2>
                      <p className="admin-muted">General system settings, SMTP, ID formats, and status.</p>
                    </div>
                  </div>
                </header>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div className="admin-card">
                      <h3>General Settings</h3>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="admin-field">
                          <span>Organization Name</span>
                          <input value={systemSettings?.organizationName ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, organizationName: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>System Name</span>
                          <input value={systemSettings?.systemName ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, systemName: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>Contact Email</span>
                          <input value={systemSettings?.contactEmail ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, contactEmail: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>Support Phone</span>
                          <input value={systemSettings?.supportPhone ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, supportPhone: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>Address</span>
                          <textarea value={systemSettings?.address ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, address: e.target.value }))} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="admin-button is-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</button>
                        </div>
                      </div>
                    </div>

                    <div className="admin-card" style={{ marginTop: 12 }}>
                      <h3>ID & Card Settings</h3>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="admin-field">
                          <span>Member ID Format</span>
                          <input value={systemSettings?.memberIdFormat ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, memberIdFormat: e.target.value }))} />
                          <small className="admin-muted">Use {`{YEAR}`} and {`{NUMBER}`} placeholders</small>
                        </div>
                        <div className="admin-field">
                          <span>Digital ID Format</span>
                          <input value={systemSettings?.digitalIdFormat ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, digitalIdFormat: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>Card Price</span>
                          <input className="admin-small-input" type="number" value={systemSettings?.cardPrice ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, cardPrice: Number(e.target.value || 0) }))} />
                          <small className="admin-muted">Price in PHP for physical cards</small>
                        </div>
                        <label className="admin-setting-item">
                          <div>
                            <strong>Auto-generate Digital IDs</strong>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.autoGenerateDigitalIds ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, autoGenerateDigitalIds: !s?.autoGenerateDigitalIds }))}><span /></button>
                        </label>
                        <label className="admin-setting-item">
                          <div>
                            <strong>Require Photo Upload</strong>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.requirePhotoUpload ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, requirePhotoUpload: !s?.requirePhotoUpload }))}><span /></button>
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="admin-button is-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="admin-card">
                      <h3>Email Settings</h3>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="admin-field">
                          <span>SMTP Server</span>
                          <input value={systemSettings?.smtpServer ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, smtpServer: e.target.value }))} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <div className="admin-field" style={{ flex: 1 }}>
                            <span>Port</span>
                            <input className="admin-small-input" type="number" value={systemSettings?.smtpPort ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, smtpPort: Number(e.target.value || 0) }))} />
                          </div>
                          <div className="admin-field" style={{ flex: 1 }}>
                            <span>Security</span>
                            <select className="admin-small-input" value={systemSettings?.smtpSecurity ?? 'TLS'} onChange={(e) => setSystemSettings((s) => ({ ...s, smtpSecurity: e.target.value }))}>
                              <option>TLS</option>
                              <option>SSL</option>
                              <option>None</option>
                            </select>
                          </div>
                        </div>
                        <div className="admin-field">
                          <span>Username</span>
                          <input value={systemSettings?.smtpUsername ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, smtpUsername: e.target.value }))} />
                        </div>
                        <div className="admin-field">
                          <span>Password</span>
                          <input type="password" value={systemSettings?.smtpPassword ?? ''} onChange={(e) => setSystemSettings((s) => ({ ...s, smtpPassword: e.target.value }))} />
                        </div>
                        <label className="admin-setting-item">
                          <div>
                            <strong>Email Notifications</strong>
                            <p className="admin-muted">Enable outgoing system email notifications</p>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.smtpEmailNotifications ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, smtpEmailNotifications: !s?.smtpEmailNotifications }))}><span /></button>
                        </label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button type="button" className="admin-button" onClick={handleTestSmtp} disabled={smtpTesting}>{smtpTesting ? 'Testing…' : 'Test Connection'}</button>
                          <button type="button" className="admin-button is-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</button>
                          {smtpTestMessage ? <div style={{ marginLeft: 8 }} className="admin-muted">{smtpTestMessage}</div> : null}
                        </div>
                      </div>
                    </div>

                    <div className="admin-card" style={{ marginTop: 12 }}>
                      <h3>System Status</h3>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <div className="admin-muted">Database Status</div>
                            <div>{systemSettings?.systemStatus?.dbStatus ?? 'Unknown'}</div>
                          </div>
                          <div>{(systemSettings?.systemStatus?.dbStatus ?? '').toLowerCase() === 'healthy' ? <span className="admin-chip is-green">Healthy</span> : null}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div className="admin-muted">Last Backup</div>
                          <div>{systemSettings?.systemStatus?.lastBackup ?? '—'}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div className="admin-muted">Storage Used</div>
                          <div>{systemSettings?.systemStatus?.storageUsed ?? '—'}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div className="admin-muted">Active Users</div>
                          <div>{systemSettings?.systemStatus?.activeUsers ?? '—'}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div className="admin-muted">System Uptime</div>
                          <div>{systemSettings?.systemStatus?.uptime ?? '—'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="admin-button">Backup Now</button>
                          <button type="button" className="admin-button">System Info</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : activeTab === 'data-privacy' ? (
            <>
              <section className="admin-surface admin-stack-md">
                <header className="admin-row">
                  <div className="admin-heading-inline">
                    <Gauge size={18} />
                    <div>
                      <h2>Data & Privacy</h2>
                      <p className="admin-muted">Manage retention, backups, and data requests.</p>
                    </div>
                  </div>
                </header>

                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 12 }}>
                    <div className="admin-card">
                      <h3>Data Privacy Controls</h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <div className="admin-field">
                          <span>Data Retention Period</span>
                          <select className="admin-small-input" value={systemSettings?.dataRetentionPeriod ?? '7 years'} onChange={(e) => setSystemSettings((s) => ({ ...s, dataRetentionPeriod: e.target.value }))}>
                            <option>1 year</option>
                            <option>2 years</option>
                            <option>3 years</option>
                            <option>5 years</option>
                            <option>7 years</option>
                          </select>
                          <div className="admin-muted">How long to keep inactive member data</div>
                        </div>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Auto-delete Logs</strong>
                            <p className="admin-muted">Automatically delete old audit logs</p>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.autoDeleteLogs ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, autoDeleteLogs: !s?.autoDeleteLogs }))}><span /></button>
                        </label>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Anonymous Analytics</strong>
                            <p className="admin-muted">Collect anonymized usage data</p>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.anonymousAnalytics ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, anonymousAnalytics: !s?.anonymousAnalytics }))}><span /></button>
                        </label>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Data Encryption</strong>
                            <p className="admin-muted">Encrypt sensitive member data</p>
                          </div>
                          <button type="button" className={`admin-toggle ${systemSettings?.dataEncryption ? 'active' : ''}`.trim()} onClick={() => setSystemSettings((s) => ({ ...s, dataEncryption: !s?.dataEncryption }))}><span /></button>
                        </label>
                      </div>
                    </div>

                    <div className="admin-card">
                      <h3>Data Export & Backup</h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <div className="admin-field">
                          <span>Automatic Backups</span>
                          <select className="admin-small-input" value={systemSettings?.backupSchedule ?? 'Daily'} onChange={(e) => setSystemSettings((s) => ({ ...s, backupSchedule: e.target.value }))}>
                            <option>Daily</option>
                            <option>Weekly</option>
                            <option>Monthly</option>
                          </select>
                        </div>
                        <div className="admin-field">
                          <span>Backup Retention</span>
                          <select className="admin-small-input" value={systemSettings?.backupRetention ?? '30 days'} onChange={(e) => setSystemSettings((s) => ({ ...s, backupRetention: e.target.value }))}>
                            <option>7 days</option>
                            <option>30 days</option>
                            <option>90 days</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <button type="button" className="admin-button is-primary" onClick={handleCreateBackup} disabled={backupInProgress}>{backupInProgress ? 'Creating…' : 'Create Full Backup'}</button>
                          <button type="button" className="admin-button" onClick={handleExportData} disabled={exportInProgress}>{exportInProgress ? 'Exporting…' : 'Export All Data'}</button>
                          {backupMessage ? <div className="admin-muted">{backupMessage}</div> : null}
                          {exportMessage ? <div className="admin-muted">{exportMessage}</div> : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="admin-card" style={{ marginTop: 8 }}>
                    <h3>Data Requests</h3>
                    <p className="admin-muted">Handle member requests for data access, correction, or deletion</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                      <div className="admin-card" style={{ padding: 16 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, textAlign: 'center' }}>{systemSettings?.dataRequests?.pending ?? 0}</div>
                        <div style={{ textAlign: 'center', marginTop: 8 }} className="admin-muted">Pending Data Requests</div>
                      </div>
                      <div className="admin-card" style={{ padding: 16 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, textAlign: 'center' }}>{systemSettings?.dataRequests?.processedThisMonth ?? 0}</div>
                        <div style={{ textAlign: 'center', marginTop: 8 }} className="admin-muted">Processed This Month</div>
                      </div>
                      <div className="admin-card" style={{ padding: 16 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, textAlign: 'center' }}>{systemSettings?.dataRequests?.avgDaysToProcess ?? '—'}</div>
                        <div style={{ textAlign: 'center', marginTop: 8 }} className="admin-muted">Avg. Days to Process</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <button type="button" className="admin-button is-primary" onClick={() => navigate('/admin/data-requests')}>View Data Requests</button>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : activeTab === '__removed-ai-settings__' ? (
            <>
              <section className="admin-surface admin-stack-md">
                <header className="admin-row">
                  <div className="admin-heading-inline">
                    <Gauge size={18} />
                    <div>
                      <h2>AI Settings & Controls</h2>
                      <p className="admin-muted">Configure automation behavior, model selection, and privacy controls.</p>
                    </div>
                  </div>
                </header>

                <div>
                  <div style={{ marginBottom: 12 }} className="reports-tab-strip">
                    <button type="button" className={`reports-tab ${aiSubTab === 'automation' ? 'is-active' : ''}`.trim()} onClick={() => setAiSubTab('automation')}>Automation</button>
                    <button type="button" className={`reports-tab ${aiSubTab === 'model' ? 'is-active' : ''}`.trim()} onClick={() => setAiSubTab('model')}>Model & API</button>
                    <button type="button" className={`reports-tab ${aiSubTab === 'privacy' ? 'is-active' : ''}`.trim()} onClick={() => setAiSubTab('privacy')}>Privacy & Redaction</button>
                    <button type="button" className={`reports-tab ${aiSubTab === 'active' ? 'is-active' : ''}`.trim()} onClick={() => setAiSubTab('active')}>Active Learning</button>
                  </div>

                  {aiSubTab === 'automation' && (
                    <div className="admin-card">
                      <h3>Automation Controls</h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <strong>Confidence Threshold</strong>
                          </div>
                          <div style={{ paddingTop: 8 }}>
                            <ConfidenceBar value={aiAutomation.confidenceThreshold ?? 0.85} showNumeric={false} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                              <div style={{ position: 'relative', flex: 1 }}>
                                <input
                                  type="range"
                                  min={0}
                                  max={1}
                                  step={0.01}
                                  value={aiAutomation.confidenceThreshold ?? 0.85}
                                  onChange={(e) => {
                                    const v = Number(e.target.value);
                                    setAiAutomation((p) => ({ ...p, confidenceThreshold: v }));
                                    setAiDirty(true);
                                    setConfidencePointerValue(v);
                                  }}
                                  onMouseEnter={() => setConfidenceHover(true)}
                                  onMouseLeave={() => setConfidenceHover(false)}
                                  onMouseMove={(e) => {
                                    const v = Number(e.target.value);
                                    setConfidencePointerValue(v);
                                  }}
                                  onMouseDown={() => setConfidenceDrag(true)}
                                  onMouseUp={() => setConfidenceDrag(false)}
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div style={{ width: 56, textAlign: 'right' }}>
                                <div style={{ border: '1px solid var(--admin-card-border)', padding: '6px 8px', borderRadius: 6, background: 'var(--admin-surface)' }}>
                                  {(aiAutomation.confidenceThreshold ?? 0.85).toFixed(2)}
                                </div>
                              </div>
                            </div>

                            <div style={{ position: 'relative', marginTop: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 0, fontSize: 12 }}>
                                <div style={{ textAlign: 'center', color: 'var(--admin-muted)' }}>0.85 (Recommended)</div>
                                <div style={{ flex: 1 }} />
                                <div style={{ textAlign: 'right', color: 'var(--admin-muted)' }}>1.00 (High)</div>
                              </div>

                              <div style={{ marginTop: 8 }} className="admin-muted">Tickets with AI confidence above this threshold will be auto-assigned. Recommended: 0.85 for optimal balance of automation and accuracy.</div>

                              {/* tooltip that follows the slider handle */}
                              <div
                                aria-hidden
                                style={{
                                  position: 'absolute',
                                  top: -36,
                                  left: `${(Number(confidencePointerValue) * 100).toFixed(2)}%`,
                                  transform: 'translateX(-50%)',
                                  pointerEvents: 'none',
                                  opacity: (confidenceHover || confidenceDrag) ? 1 : 0,
                                  transition: 'opacity 120ms ease, top 120ms ease',
                                }}
                              >
                                <div style={{ border: '1px solid var(--admin-card-border)', padding: '6px 8px', borderRadius: 6, background: 'var(--admin-surface)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                                  {(Number(confidencePointerValue) ?? 0).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Auto-assign when confidence ≥ threshold</strong>
                            <p className="admin-muted">Automatically assign tickets when AI confidence meets threshold</p>
                          </div>
                          <button type="button" className={`admin-toggle ${aiAutomation.autoAssign ? 'active' : ''}`.trim()} onClick={() => toggleAiAutomation('autoAssign')}>
                            <span />
                          </button>
                        </label>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Auto-generate response proposals</strong>
                            <p className="admin-muted">Generate draft responses for proponent review and approval</p>
                          </div>
                          <button type="button" className={`admin-toggle ${aiAutomation.autoResolve ? 'active' : ''}`.trim()} onClick={() => toggleAiAutomation('autoResolve')}>
                            <span />
                          </button>
                        </label>
                      </div>
                    </div>
                  )}

                  {aiSubTab === 'model' && (
                    <div className="admin-card" style={{ marginTop: 0 }}>
                      <h3>Model Configuration</h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <div className="admin-field">
                          <span>AI Model</span>
                          <select className="admin-small-input" value={aiModelRollouts[0]?.name ?? 'GPT-4'} onChange={(e) => { /* no-op for now */ }}>
                            <option>GPT-4</option>
                            <option>GPT-4o</option>
                            <option>GPT-3.5-Turbo</option>
                          </select>
                        </div>

                        <div className="admin-field">
                          <span>Token Limit</span>
                          <input className="admin-small-input" value={4000} onChange={() => {}} />
                          <small className="admin-muted">Maximum tokens per API call. Higher values allow for more context but increase cost.</small>
                        </div>

                        <div className="admin-field">
                          <span>n8n Webhook URL</span>
                          <input className="admin-small-input" value={''} onChange={() => {}} />
                          <small className="admin-muted">Webhook endpoint for n8n integration. URL is masked for security.</small>
                        </div>

                        <label className="admin-setting-item">
                          <div>
                            <strong>Debug Mode</strong>
                            <p className="admin-muted">Enable detailed logging for troubleshooting (increases log verbosity)</p>
                          </div>
                          <button type="button" className={`admin-toggle ${aiAutomation.auditLogging ? 'active' : ''}`.trim()} onClick={() => toggleAiAutomation('auditLogging')}>
                            <span />
                          </button>
                        </label>
                      </div>
                    </div>
                  )}

                  {aiSubTab === 'privacy' && (
                    <div className="admin-card" style={{ marginTop: 0 }}>
                      <h3>Privacy & Redaction</h3>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="admin-field" style={{ marginBottom: 0 }}>
                          <span>Redaction Policy</span>
                          <div style={{ padding: 12, border: '1px solid var(--admin-card-border)', borderRadius: 8, background: 'var(--admin-surface)' }}>
                            <strong>Strict</strong>
                            <div style={{ marginTop: 8 }} className="admin-muted">All PII redacted including names, IDs, addresses, phones, emails</div>
                            <div style={{ marginTop: 12 }}>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <span className="admin-chip">Names</span>
                                <span className="admin-chip">ID Numbers</span>
                                <span className="admin-chip">Addresses</span>
                                <span className="admin-chip">Phone Numbers</span>
                                <span className="admin-chip">Email Addresses</span>
                                <span className="admin-chip">Medical Info</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Always require human review for sensitive categories */}
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>Always Require Human Review (Sensitive Categories)</div>
                          <div className="admin-muted" style={{ marginBottom: 8 }}>These categories will always be marked for human review regardless of confidence level</div>
                          <div style={{ padding: 16, border: '1px solid var(--admin-card-border)', borderRadius: 8, background: 'var(--admin-surface)' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <span className="admin-chip">Legal - Complex</span>
                              <span className="admin-chip">Medical - Rare</span>
                              <span className="admin-chip">Emergency - Critical</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {aiSubTab === 'active' && (
                    <div className="admin-card" style={{ marginTop: 0 }}>
                      <h3>Active Learning Configuration</h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <label className="admin-setting-item">
                          <div>
                            <strong>Enable Active Learning</strong>
                            <p className="admin-muted">Use admin overrides and corrections to improve AI model accuracy</p>
                          </div>
                          <button type="button" className={`admin-toggle ${aiAutomation.enableActiveLearning ? 'active' : ''}`.trim()} onClick={() => { toggleAiAutomation('enableActiveLearning'); }}>
                            <span />
                          </button>
                        </label>

                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <strong>Sampling Rate</strong>
                            <div style={{ minWidth: 56, textAlign: 'right' }}>
                              <div style={{ border: '1px solid var(--admin-card-border)', padding: '6px 8px', borderRadius: 6, background: 'var(--admin-surface)', opacity: aiAutomation.enableActiveLearning ? 1 : 0.6 }}>
                                {Math.round((aiModelRollouts?.[0]?.coverage ?? 0) * 100)}%
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round((aiModelRollouts?.[0]?.coverage ?? 0) * 100)}
                              onChange={(e) => {
                                const pct = Number(e.target.value) || 0;
                                setAiModelRollouts((prev) => {
                                  const next = Array.isArray(prev) ? [...prev] : [];
                                  const coverage = Math.min(Math.max(pct / 100, 0), 1);
                                  if (next.length === 0) {
                                    next[0] = { id: `model-0`, name: 'default', capabilities: [], coverage };
                                  } else {
                                    next[0] = { ...next[0], coverage };
                                  }
                                  return next;
                                });
                                setAiDirty(true);
                              }}
                              style={{ width: '100%', marginTop: 0, opacity: aiAutomation.enableActiveLearning ? 1 : 0.5 }}
                              disabled={!aiAutomation.enableActiveLearning}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </>
          ) : null}

        {activeTab === "configuration" ? (
          <>
            <header className="admin-row">
              <div className="admin-heading-inline">
                <Bell size={18} />
                <div>
                  <h2>Notifications</h2>
                  <p className="admin-muted">
                    Configure alerts sent to the admin team when critical events occur.
                  </p>
                </div>
              </div>
            </header>
            <div className="admin-setting-list">
              <label htmlFor="dailyDigest" className="admin-setting-item">
                <div>
                  <strong>Daily email digest</strong>
                  <p className="admin-muted">
                    Receive a morning summary of pending approvals and escalations.
                  </p>
                </div>
                <button
                  type="button"
                  id="dailyDigest"
                  className={`admin-toggle ${notifications.dailyDigest ? "active" : ""}`.trim()}
                  onClick={() => toggle("dailyDigest")}
                  disabled={loading || saving}
                >
                  <span />
                </button>
              </label>

              <label htmlFor="aiSummary" className="admin-setting-item">
                <div>
                  <strong>Include AI summary with alerts</strong>
                  <p className="admin-muted">
                    Attach short bullet points of AI-detected highlights when sending notifications.
                  </p>
                </div>
                <button
                  type="button"
                  id="aiSummary"
                  className={`admin-toggle ${notifications.aiSummary ? "active" : ""}`.trim()}
                  onClick={() => toggle("aiSummary")}
                  disabled={loading || saving}
                >
                  <span />
                </button>
              </label>

              <label htmlFor="escalationSms" className="admin-setting-item">
                <div>
                  <strong>Escalation SMS alerts</strong>
                  <p className="admin-muted">
                    Text-message the duty officer if a ticket remains unassigned after escalation.
                  </p>
                </div>
                <button
                  type="button"
                  id="escalationSms"
                  className={`admin-toggle ${notifications.escalationSms ? "active" : ""}`.trim()}
                  onClick={() => toggle("escalationSms")}
                  disabled={loading || saving}
                >
                  <span />
                </button>
              </label>
            </div>

            <div style={{ borderTop: "1px solid var(--admin-card-border)", marginTop: 10, paddingTop: 6 }}>
              <header className="admin-row">
                <div className="admin-heading-inline">
                  <Shield size={18} />
                  <div>
                    <h2>Security Settings</h2>
                    <p className="admin-muted">
                      Configure safeguards that apply to every administrator account.
                    </p>
                  </div>
                </div>
              </header>

              <div className="admin-setting-list">
                <div className="admin-setting-item">
                  <div>
                    <strong>Two-Factor Authentication</strong>
                    <p className="admin-muted">Require 2FA for all admin accounts.</p>
                  </div>
                  <button
                    type="button"
                    className={`admin-toggle ${securityControls.requireTwoFactor ? "active" : ""}`.trim()}
                    onClick={() => toggleSecurityFlag("requireTwoFactor")}
                    disabled={loading || saving}
                  >
                    <span />
                  </button>
                </div>

                <div className="admin-setting-item">
                  <div>
                    <strong>Session Timeout</strong>
                    <p className="admin-muted">Auto-logout after admin inactivity.</p>
                  </div>
                  <div className="admin-select">
                    <select
                      aria-label="Session timeout"
                      value={securityControls.sessionTimeoutMinutes}
                      onChange={(event) => updateSecurityControl("sessionTimeoutMinutes", Number(event.target.value))}
                      disabled={loading || saving}
                    >
                      {SESSION_TIMEOUT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="admin-setting-item">
                  <div>
                    <strong>Login Attempt Limit</strong>
                    <p className="admin-muted">Lock an account after repeated failures.</p>
                  </div>
                  <div className="admin-select">
                    <select
                      aria-label="Login attempt limit"
                      value={securityControls.loginAttemptLimit}
                      onChange={(event) => updateSecurityControl("loginAttemptLimit", Number(event.target.value))}
                      disabled={loading || saving}
                    >
                      {LOGIN_ATTEMPT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="admin-setting-item">
                  <div>
                    <strong>Member Inactivity Threshold (days)</strong>
                    <p className="admin-muted">Classify approved members as inactive when last login is older than this many days.</p>
                  </div>
                  <div className="admin-inactivity-setting__controls">
                    <div className="admin-field admin-inactivity-setting__field">
                      <span>Threshold</span>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        step={1}
                        className="admin-inactivity-setting__input"
                        value={securityControls.inactivityThresholdDays}
                        onChange={(event) => updateSecurityControl(
                          "inactivityThresholdDays",
                          normalizeInactivityThresholdDays(event.target.value),
                        )}
                        disabled={loading || saving || inactivitySettingSaving || !isEditingInactivityThreshold}
                        aria-label="Member inactivity threshold in days"
                      />
                    </div>
                    <button
                      type="button"
                      className="admin-button is-primary admin-inactivity-setting__edit-button"
                      onClick={handleInactivityThresholdAction}
                      disabled={loading || saving || inactivitySettingSaving}
                    >
                      {inactivitySettingSaving ? <Loader2 size={16} className="admin-spin" /> : (isEditingInactivityThreshold ? <Save size={16} /> : <Edit size={16} />)}
                      {inactivitySettingSaving ? "Saving..." : (isEditingInactivityThreshold ? "Save" : "Edit")}
                    </button>
                  </div>
                </div>
                {inactivitySettingError ? <p className="admin-alert is-error admin-inactivity-setting__status-alert">{inactivitySettingError}</p> : null}
                {inactivitySettingSuccess ? <p className="admin-alert is-success admin-inactivity-setting__status-alert">{inactivitySettingSuccess}</p> : null}

                <div className="admin-setting-item">
                  <div>
                    <strong>Audit Logging</strong>
                    <p className="admin-muted">Track sensitive changes for compliance.</p>
                  </div>
                  <button
                    type="button"
                    className={`admin-toggle ${securityControls.auditLoggingEnabled ? "active" : ""}`.trim()}
                    onClick={() => toggleSecurityFlag("auditLoggingEnabled")}
                    disabled={loading || saving}
                  >
                    <span />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}

        </section>

        

      </div>

      <SudoModeModal
        open={sudoModalOpen}
        onSuccess={onSudoSuccess}
        onCancel={onSudoCancel}
      />

      {mfaResetModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div
            className="admin-dialog__backdrop"
            onClick={mfaResetPendingId ? undefined : closeMfaResetModal}
            role="presentation"
          />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Reset MFA</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={closeMfaResetModal}
                disabled={Boolean(mfaResetPendingId)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="admin-dialog__body">
              <p className="admin-muted" style={{ marginTop: 0 }}>
                Reset MFA for <strong>{mfaResetTargetAdmin?.email ?? "this admin"}</strong>? They will be signed out and must reconfigure MFA on next login.
              </p>

              <div className="admin-field">
                <span>Reason for audit log</span>
                <textarea
                  value={mfaResetReason}
                  onChange={(event) => setMfaResetReason(event.target.value)}
                  placeholder="Provide reason (minimum 10 characters)"
                  disabled={Boolean(mfaResetPendingId)}
                  rows={3}
                />
                <small className="admin-muted">
                  This reason is required and will be stored in audit logs.
                </small>
              </div>

              {mfaResetModalError ? <div className="admin-alert is-error">{mfaResetModalError}</div> : null}

              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button"
                  onClick={closeMfaResetModal}
                  disabled={Boolean(mfaResetPendingId)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={submitMfaReset}
                  disabled={Boolean(mfaResetPendingId)}
                >
                  {mfaResetPendingId ? <Loader2 size={16} /> : <Shield size={16} />}
                  Confirm Reset MFA
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {mfaResetSuccessModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div
            className="admin-dialog__backdrop"
            onClick={() => setMfaResetSuccessModalOpen(false)}
            role="presentation"
          />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>MFA Reset Successful</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => setMfaResetSuccessModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-dialog__body">
              <p className="admin-muted" style={{ marginTop: 0 }}>{mfaResetSuccessMessage}</p>
              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={() => setMfaResetSuccessModalOpen(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {adminModalOpen ? (
          <div className="admin-dialog" role="dialog" aria-modal="true">
            <div
              className="admin-dialog__backdrop"
              onClick={adminModalSaving ? undefined : closeAdminModal}
              role="presentation"
            />
            <div className="admin-dialog__panel">
              <div className="admin-dialog__header">
                <h3>
                  {adminModalMode === "create"
                    ? "Add New Admin"
                    : `Edit Admin: ${selectedAdmin?.fullName ?? selectedAdmin?.email ?? ""}`}
                </h3>
                <button
                  type="button"
                  className="admin-dialog__close"
                  onClick={closeAdminModal}
                  disabled={adminModalSaving}
                  aria-label="Close admin modal"
                >
                  <X size={16} />
                </button>
              </div>
              <form onSubmit={handleAdminSubmit}>
                <div className="admin-dialog__body">
                <div className="admin-form-section">
                  <div className={`admin-field${adminModalFieldErrors.email ? " has-error" : ""}`}>
                    <span>Email</span>
                    <input
                      type="email"
                      value={adminForm.email}
                      onChange={(event) => updateAdminForm("email", event.target.value)}
                      placeholder="admin@example.org"
                      required
                      disabled={adminModalSaving}
                    />
                    {adminModalFieldErrors.email ? (
                      <small className="admin-field__error">{adminModalFieldErrors.email}</small>
                    ) : null}
                  </div>
                  <div className={`admin-field${adminModalFieldErrors.firstName ? " has-error" : ""}`}>
                    <span>First Name</span>
                    <input
                      value={adminForm.firstName}
                      onChange={(event) => updateAdminForm("firstName", event.target.value)}
                      placeholder="Given name"
                      required
                      disabled={adminModalSaving}
                    />
                    {adminModalFieldErrors.firstName ? (
                      <small className="admin-field__error">{adminModalFieldErrors.firstName}</small>
                    ) : null}
                  </div>
                  <div className={`admin-field${adminModalFieldErrors.lastName ? " has-error" : ""}`}>
                    <span>Last Name</span>
                    <input
                      value={adminForm.lastName}
                      onChange={(event) => updateAdminForm("lastName", event.target.value)}
                      placeholder="Family name"
                      required
                      disabled={adminModalSaving}
                    />
                    {adminModalFieldErrors.lastName ? (
                      <small className="admin-field__error">{adminModalFieldErrors.lastName}</small>
                    ) : null}
                  </div>
                  <div className="admin-field">
                    <span>Status</span>
                    <select
                      value={adminForm.status}
                      onChange={(event) => updateAdminForm("status", event.target.value)}
                      disabled={adminModalSaving}
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {adminModalMode === "create" ? (
                    <div className={`admin-field${adminModalFieldErrors.password ? " has-error" : ""}`}>
                      <span>New Password</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={adminForm.password}
                          onChange={(event) => updateAdminForm("password", event.target.value)}
                          onFocus={() => handleAdminPasswordFocus("password")}
                          onBlur={() => handleAdminPasswordBlur("password")}
                          placeholder="Minimum 8 characters"
                          required
                          disabled={adminModalSaving}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="admin-icon-button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          disabled={adminModalSaving}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {adminModalFieldErrors.password ? (
                        <small className="admin-field__error">{adminModalFieldErrors.password}</small>
                      ) : null}
                      <small>The admin will be prompted to change this password on first login.</small>
                    </div>
                  ) : (
                    <div className="admin-field">
                      <span>Password</span>
                      <input value="********" readOnly disabled />
                      <small>Use the password reset workflow to change credentials.</small>
                    </div>
                  )}

                  {adminModalMode === "create" ? (
                    <div className={`admin-field${adminModalFieldErrors.confirmPassword ? " has-error" : ""}`}>
                      <span>Confirm New Password</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={adminForm.confirmPassword}
                          onChange={(event) => updateAdminForm("confirmPassword", event.target.value)}
                          onFocus={() => handleAdminPasswordFocus("confirm")}
                          onBlur={() => handleAdminPasswordBlur("confirm")}
                          placeholder="Re-enter the password"
                          required
                          disabled={adminModalSaving || validatePassword(adminForm.password) !== null}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="admin-icon-button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          disabled={adminModalSaving || validatePassword(adminForm.password) !== null}
                          aria-label={showConfirmPassword ? "Hide confirmation" : "Show confirmation"}
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {adminModalFieldErrors.confirmPassword ? (
                        <small className="admin-field__error">{adminModalFieldErrors.confirmPassword}</small>
                      ) : null}
                      {validatePassword(adminForm.password) !== null ? (
                        <small className="admin-muted">Please ensure the password above meets all requirements first.</small>
                      ) : null}
                      {((adminPasswordFocused || adminConfirmFocused || adminForm.password || adminForm.confirmPassword)) ? (
                        <PasswordRequirements
                          password={adminConfirmFocused && adminForm.confirmPassword ? adminForm.confirmPassword : (adminForm.password || "")}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="admin-form-section">
                  <div>
                    <strong>Role assignments</strong>
                    <p className="admin-muted" style={{ margin: 0 }}>
                      Select roles that determine which modules the admin can access.
                    </p>
                  </div>
                  {visibleRoles.length ? (
                    visibleRoles.map((role) => {
                      const isChecked = (adminForm.roles ?? []).includes(role.id);
                      return (
                        <label key={role.id} className="admin-role-option">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRoleSelection(role.id)}
                            disabled={adminModalSaving}
                          />
                          <div>
                            <strong>{role.name}</strong>
                            <small>{role.description ?? "No description provided."}</small>
                            <span className="admin-chip is-blue">{role.adminCount} assigned</span>
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <span className="admin-muted">No roles available. Create roles to manage permissions.</span>
                  )}
                </div>

                {adminModalError ? <div className="admin-alert is-error">{adminModalError}</div> : null}
				</div>

                <div className="admin-dialog__footer">
                  <button
                    type="button"
                    className="admin-button"
                    onClick={closeAdminModal}
                    disabled={adminModalSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-button is-primary"
                    disabled={adminModalSaving}
                  >
                    {adminModalSaving ? <Loader2 size={16} /> : <Save size={16} />}
                    {adminModalMode === "create" ? "Create Admin" : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

      {deptModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div
            className="admin-dialog__backdrop"
            onClick={deptModalSaving ? undefined : closeDeptModal}
            role="presentation"
          />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>
                {deptModalMode === "create"
                  ? "Create Department Account"
                  : `Edit Department Account: ${selectedDeptAccount?.email ?? ""}`}
              </h3>
              <button
                type="button"
                className="admin-dialog__close"
                onClick={closeDeptModal}
                disabled={deptModalSaving}
                aria-label="Close department account modal"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleDeptSubmit}>
              <div className="admin-dialog__body">
              <div className="admin-form-section">
                <div className="admin-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={deptForm.email}
                    onChange={(e) => setDeptForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="department@example.org"
                    required
                    disabled={deptModalSaving}
                  />
                </div>
                <div className="admin-field">
                  <span>First Name</span>
                  <input
                    value={deptForm.firstName}
                    onChange={(e) => setDeptForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Given name"
                    required
                    disabled={deptModalSaving}
                  />
                </div>
                <div className="admin-field">
                  <span>Last Name</span>
                  <input
                    value={deptForm.lastName}
                    onChange={(e) => setDeptForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Family name"
                    required
                    disabled={deptModalSaving}
                  />
                </div>
                {deptModalMode === "create" ? (
                  <div className="admin-field">
                    <span>Password</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type={showDeptPassword ? "text" : "password"}
                        value={deptForm.password}
                        onChange={(e) => setDeptForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Minimum 8 characters"
                        required
                        disabled={deptModalSaving}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => setShowDeptPassword((prev) => !prev)}
                        disabled={deptModalSaving}
                        aria-label={showDeptPassword ? "Hide password" : "Show password"}
                      >
                        {showDeptPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <small>The department account will be prompted to change this password on first login.</small>
                  </div>
                ) : null}
                {deptModalMode === "create" ? (
                  <div className="admin-field">
                    <span>Confirm Password</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type={showDeptConfirmPassword ? "text" : "password"}
                        value={deptForm.confirmPassword}
                        onChange={(e) => setDeptForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Re-enter the password"
                        required
                        disabled={deptModalSaving}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => setShowDeptConfirmPassword((prev) => !prev)}
                        disabled={deptModalSaving}
                        aria-label={showDeptConfirmPassword ? "Hide confirmation" : "Show confirmation"}
                      >
                        {showDeptConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {deptForm.confirmPassword && deptForm.password !== deptForm.confirmPassword ? (
                      <small className="admin-field__error">Passwords do not match.</small>
                    ) : null}
                  </div>
                ) : null}
                <div className="admin-field">
                  <span>Department</span>
                  <select
                    value={deptForm.department}
                    onChange={(e) => setDeptForm((prev) => ({ ...prev, department: e.target.value }))}
                    disabled={deptModalSaving}
                  >
                    {DEPARTMENT_OPTIONS.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept.charAt(0).toUpperCase() + dept.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-field">
                  <span>Status</span>
                  <select
                    value={deptForm.status}
                    onChange={(e) => setDeptForm((prev) => ({ ...prev, status: e.target.value }))}
                    disabled={deptModalSaving}
                  >
                    {DEPT_ACCOUNT_STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {deptModalError ? <div className="admin-alert is-error">{deptModalError}</div> : null}
			</div>

              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button"
                  onClick={closeDeptModal}
                  disabled={deptModalSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-button is-primary"
                  disabled={deptModalSaving}
                >
                  {deptModalSaving ? <Loader2 size={16} /> : <Save size={16} />}
                  {deptModalMode === "create" ? "Create Account" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deptMfaResetModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div
            className="admin-dialog__backdrop"
            onClick={deptMfaResetPendingId ? undefined : closeDeptMfaResetModal}
            role="presentation"
          />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Reset MFA</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={closeDeptMfaResetModal}
                disabled={Boolean(deptMfaResetPendingId)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-dialog__body">
              <p className="admin-muted" style={{ marginTop: 0 }}>
                Reset MFA for <strong>{deptMfaResetTarget?.email ?? "this department account"}</strong>? They will be signed out and must reconfigure MFA on next login.
              </p>
              <div className="admin-field">
                <span>Reason for audit log</span>
                <textarea
                  value={deptMfaResetReason}
                  onChange={(e) => setDeptMfaResetReason(e.target.value)}
                  placeholder="Provide reason (minimum 10 characters)"
                  disabled={Boolean(deptMfaResetPendingId)}
                  rows={3}
                />
                <small className="admin-muted">
                  This reason is required and will be stored in audit logs.
                </small>
              </div>
              {deptMfaResetModalError ? <div className="admin-alert is-error">{deptMfaResetModalError}</div> : null}
              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button"
                  onClick={closeDeptMfaResetModal}
                  disabled={Boolean(deptMfaResetPendingId)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={submitDeptMfaReset}
                  disabled={Boolean(deptMfaResetPendingId)}
                >
                  {deptMfaResetPendingId ? <Loader2 size={16} /> : <Shield size={16} />}
                  Confirm Reset MFA
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deptMfaResetSuccessModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div
            className="admin-dialog__backdrop"
            onClick={() => setDeptMfaResetSuccessModalOpen(false)}
            role="presentation"
          />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>MFA Reset Successful</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => setDeptMfaResetSuccessModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-dialog__body">
              <p className="admin-muted" style={{ marginTop: 0 }}>{deptMfaResetSuccessMessage}</p>
              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={() => setDeptMfaResetSuccessModalOpen(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirmModal.isOpen ? (
        <div className="admin-dialog admin-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-delete-confirm-title">
          <div
            className="admin-dialog__backdrop"
            onClick={deleteConfirmPending ? undefined : closeDeleteConfirmModal}
            role="presentation"
          />
          <div className="admin-dialog__panel admin-delete-dialog__panel">
            <div className="admin-dialog__header">
              <h3 id="admin-delete-confirm-title">{deleteConfirmModal.title}</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={closeDeleteConfirmModal}
                disabled={deleteConfirmPending}
                aria-label="Close delete confirmation"
              >
                <X size={16} />
              </button>
            </div>
            <div className="admin-dialog__body">
              <p className="admin-delete-dialog__message">{deleteConfirmModal.message}</p>
              {deleteConfirmError ? <div className="admin-alert is-error">{deleteConfirmError}</div> : null}
              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button"
                  onClick={closeDeleteConfirmModal}
                  disabled={deleteConfirmPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-button is-danger"
                  onClick={submitDeleteConfirmation}
                  disabled={deleteConfirmPending}
                >
                  {deleteConfirmPending ? <Loader2 size={16} /> : <Trash2 size={16} />}
                  {deleteConfirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Union Management Modals ── */}

      {/* Add Affiliation Modal */}
      {umModal.type === 'addAffiliation' && (() => {
        const previewAcronym = (umModalForm.acronym || '').trim().toUpperCase() || 'UNION';
        const previewFullName = (umModalForm.fullName || '').trim() || 'Union Affiliation Name';
        const selectedTheme = COLOR_THEMES[umModalForm.colorTheme || 'red'] || COLOR_THEMES.red;
        const selectedThemeStops = extractThemeStops(selectedTheme);
        const previewStartColor = normalizeHexColor(umModalForm.gradientStart, selectedThemeStops.start);
        const previewEndColor = normalizeHexColor(umModalForm.gradientEnd, selectedThemeStops.end);
        const previewHeaderGradient = `linear-gradient(135deg, ${previewStartColor}, ${previewEndColor})`;
        const previewTheme = {
          ...selectedTheme,
          headerGradient: previewHeaderGradient,
          headerBackGradient: previewHeaderGradient,
        };
        const previewLogo = umLogoPreview || defaultIdLogo;
        const previewMemberId = `${previewAcronym}-CPCCU-2026-780772`;
        const previewQrRef = `QR-26780772`;
        return (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel um-affiliation-wide-panel">
            <div className="admin-dialog__header">
              <h3>Add Union Affiliation</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body um-affiliation-body">
              {/* Left: Form */}
              <div className="um-affiliation-form">
                <label className="admin-field">
                  <span className="admin-card__label">Full Name *</span>
                  <input className="admin-input" value={umModalForm.fullName || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, fullName: e.target.value }))} placeholder="Luzon Workers Union" />
                </label>
                <label className="admin-field">
                  <span className="admin-card__label">Acronym *</span>
                  <input className="admin-input" value={umModalForm.acronym || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, acronym: e.target.value.toUpperCase() }))} placeholder="LWU" maxLength={6} />
                  <span className="admin-muted" style={{ fontSize: '0.8rem' }}>2-6 uppercase letters</span>
                </label>
                <label className="admin-field">
                  <span className="admin-card__label">Logo (optional)</span>
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={handleUmLogoFileChange} />
                  {umLogoPreview && <img src={umLogoPreview} alt="Preview" className="um-logo-preview" />}
                </label>
                <div className="admin-field">
                  <span className="admin-card__label">ID Color Theme *</span>
                  <div className="um-theme-swatches">
                    {Object.entries(COLOR_THEMES).map(([key, val]) => (
                      <div
                        key={key}
                        title={key.charAt(0).toUpperCase() + key.slice(1)}
                        onClick={() => {
                          const stops = extractThemeStops(val);
                          setUmModalForm((f) => ({
                            ...f,
                            colorTheme: key,
                            gradientStart: stops.start,
                            gradientEnd: stops.end,
                          }));
                        }}
                        style={{
                          width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
                          background: val.headerGradient,
                          border: (umModalForm.colorTheme || 'red') === key ? '3px solid #111' : '2px solid #d1d5db',
                          transition: 'border 0.15s',
                        }}
                      />
                    ))}
                  </div>
                  <span className="admin-muted" style={{ fontSize: '0.8rem', marginTop: '4px' }}>{(umModalForm.colorTheme || 'red').charAt(0).toUpperCase() + (umModalForm.colorTheme || 'red').slice(1)}</span>
                </div>

                <div className="admin-field um-gradient-controls">
                  <div className="um-gradient-controls__header">
                    <span className="admin-card__label">Header Gradient</span>
                    <button type="button" className="admin-button admin-button--ghost admin-button--sm" onClick={handleGenerateRandomGradient}>
                      Generate Random Gradient
                    </button>
                  </div>
                  <div className="um-gradient-controls__inputs">
                    <label className="um-gradient-control">
                      <span className="admin-muted">Start Color</span>
                      <input
                        type="color"
                        value={previewStartColor}
                        onChange={(e) => setAffiliationGradientColors(e.target.value, previewEndColor)}
                      />
                    </label>
                    <label className="um-gradient-control">
                      <span className="admin-muted">End Color</span>
                      <input
                        type="color"
                        value={previewEndColor}
                        onChange={(e) => setAffiliationGradientColors(previewStartColor, e.target.value)}
                      />
                    </label>
                    <div className="um-gradient-preview" style={{ background: previewHeaderGradient }}>
                      <span>Live Gradient Preview</span>
                    </div>
                  </div>
                  <span className="admin-muted" style={{ fontSize: '0.8rem' }}>
                    Saved theme is auto-mapped to the closest supported color profile.
                  </span>
                </div>
              </div>

              {/* Right: Digital ID Preview */}
              <div className="union-affiliation-id-preview">
                <span className="admin-card__label" style={{ textAlign: 'center', display: 'block', marginBottom: '8px' }}>Digital ID Preview</span>
                <div className="um-id-preview-scale">
                <div
                  className={`digital-id-card${umIdPreviewFlipped ? ' digital-id-card--flipped' : ''}`}
                  onClick={() => setUmIdPreviewFlipped((v) => !v)}
                  role="presentation"
                >
                  <div className="digital-id-card__inner">
                    {/* FRONT */}
                    <div className="digital-id-card__face digital-id-card__face--front">
                      <div className="digital-id-card__header" style={{ background: previewTheme.headerGradient }}>
                        <div className="digital-id-card__seal">
                          <img src={previewLogo} alt={previewAcronym} />
                        </div>
                        <div>
                          <p style={{ fontSize: previewFullName.length > 30 ? '0.78rem' : '0.9rem' }}>{previewFullName.toUpperCase()}</p>
                          <span>No. 262, 15th Ave., Brgy. Silangan, Cubao, Quezon City</span>
                        </div>
                      </div>
                      <div className="digital-id-card__member-banner">SHOP STEWARD</div>
                      <div className="digital-id-card__body">
                        <div className="digital-id-card__photo">
                          <div className="digital-id-card__initials">MC</div>
                        </div>
                        <div className="digital-id-card__info">
                          <div style={{ fontSize: '1.35rem', fontWeight: 700, letterSpacing: '0.04em', textAlign: 'center', color: '#0f172a' }}>
                            JOHN SMITH
                          </div>
                          <div className="members-id-position-text">Shop Steward</div>
                          <div className="digital-id-card__meta" style={{ marginTop: '20px' }}>
                            <table className="members-id-meta-table">
                              <tbody>
                                <tr><td>ID Number</td><td>:</td><td>780772</td></tr>
                                <tr><td>Company</td><td>:</td><td>Cebu Pacific</td></tr>
                                <tr><td>Contact No.</td><td>:</td><td>0912 345 6789</td></tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="digital-id-card__signature" style={{ marginTop: '25px' }}>
                            <div className="members-id-signature-line" style={{ width: '180%' }} />
                            <span className="members-id-signature-label">Authorized Signature</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* BACK */}
                    <div className="digital-id-card__face digital-id-card__face--back">
                      <div className="digital-id-card__header digital-id-card__header--back" style={{ background: previewTheme.headerBackGradient }}>
                        <div className="digital-id-card__seal">
                          <img src={previewLogo} alt={previewAcronym} />
                        </div>
                        <div>
                          <p>{previewAcronym} DIGITAL ID</p>
                          <span>Official Verification Code</span>
                        </div>
                      </div>
                      <div className="digital-id-card__qr">
                        <div className="digital-id-card__qr-box">
                          <QrCode size={150} strokeWidth={1} />
                        </div>
                        <div className="digital-id-card__qr-info">
                          <span>{previewMemberId}</span>
                          <span className="members-id-qr-ref">QR Ref: {previewQrRef}</span>
                          <small>Scan to verify membership status</small>
                        </div>
                        <div className="digital-id-card__emergency-contact">
                          <strong>Emergency Contact</strong>
                          <span>Name: Richard Roe Smith</span>
                          <span>Mobile No: 0912 345 6789</span>
                        </div>
                      </div>
                      <div className="digital-id-card__footer" style={{ background: previewTheme.footerGradient }}>
                        <ShieldCheck size={16} />
                        <span>LaborConnect &copy; 2026 &bull; Official Digital ID</span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                <button
                  type="button"
                  className="admin-button admin-button--secondary admin-button--sm um-flip-btn"
                  onClick={(e) => { e.stopPropagation(); setUmIdPreviewFlipped((v) => !v); }}
                >
                  <RotateCw size={14} /> {umIdPreviewFlipped ? 'Show Front' : 'Show Back'}
                </button>
              </div>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving} onClick={() => {
                setUmConfirm({
                  title: 'Add Affiliation',
                  message: 'Are you sure you want to add this record?',
                  onConfirm: async () => { closeUmConfirm(); await handleAddAffiliation(); },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Add Affiliation</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Add Company Modal */}
      {umModal.type === 'addCompany' && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Add Company</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body">
              <label className="admin-field">
                <span className="admin-card__label">Union Affiliation *</span>
                <select className="admin-select" value={umModalForm.affiliationId || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, affiliationId: e.target.value }))}>
                  <option value="" disabled>Select Affiliation</option>
                  {umAffiliations.filter((a) => a.status === 'active').map((a) => (
                    <option key={a.id} value={a.id}>{a.acronym} — {a.fullName}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Company Name *</span>
                <input className="admin-input" value={umModalForm.name || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jollibee Foods Corporation" />
              </label>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving} onClick={() => {
                setUmConfirm({
                  title: 'Add Company',
                  message: 'Are you sure you want to add this record?',
                  onConfirm: async () => { closeUmConfirm(); await handleAddCompany(); },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Add Company</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Union Name Modal */}
      {umModal.type === 'addUnionName' && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Add Union Name</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body">
              <label className="admin-field">
                <span className="admin-card__label">Company *</span>
                <select className="admin-select" value={umModalForm.companyId || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, companyId: e.target.value }))}>
                  <option value="" disabled>Select Company</option>
                  {umCompanies.filter((c) => c.status === 'active').map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.affiliationAcronym})</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Full Name *</span>
                <input className="admin-input" value={umModalForm.fullName || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, fullName: e.target.value }))} placeholder="Jollibee Workers Union" />
              </label>
              <label className="admin-field">
                <span className="admin-card__label">Acronym *</span>
                <input className="admin-input" value={umModalForm.acronym || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, acronym: e.target.value.toUpperCase() }))} placeholder="JWU" maxLength={6} />
                <span className="admin-muted" style={{ fontSize: '0.8rem' }}>2-6 uppercase letters</span>
              </label>
              {umModalForm.fullName && umModalForm.acronym && (
                <div className="um-live-preview">
                  <span className="admin-card__label">Preview:</span> {umModalForm.fullName.trim()} ({(umModalForm.acronym || '').trim().toUpperCase()})
                </div>
              )}
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving} onClick={() => {
                setUmConfirm({
                  title: 'Add Union Name',
                  message: 'Are you sure you want to add this record?',
                  onConfirm: async () => { closeUmConfirm(); await handleAddUnionName(); },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Add Union Name</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Position Modal */}
      {umModal.type === 'addPosition' && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Add Union Position</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body">
              <label className="admin-field">
                <span className="admin-card__label">Position Name *</span>
                <input className="admin-input" value={umModalForm.name || ''} onChange={(e) => setUmModalForm((f) => ({ ...f, name: e.target.value }))} placeholder="Shop Steward" />
              </label>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving} onClick={() => {
                setUmConfirm({
                  title: 'Add Position',
                  message: 'Are you sure you want to add this record?',
                  onConfirm: async () => { closeUmConfirm(); await handleAddPosition(); },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Add Position</button>
            </div>
          </div>
        </div>
      )}

      {/* View Logo Modal */}
      {umModal.type === 'viewLogo' && umModal.data && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>{umModal.data.acronym} — Logo Preview</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            <div className="admin-dialog__body" style={{ textAlign: 'center' }}>
              <img src={resolveBackendAssetUrl(umModal.data.logoPath)} alt={umModal.data.acronym} className="um-logo-full" />
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Update Logo Modal */}
      {umModal.type === 'updateLogo' && umModal.data && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Update Logo — {umModal.data.acronym}</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body">
              {umModal.data.logoPath && (
                <div style={{ marginBottom: '1rem' }}>
                  <p className="admin-card__label">Current Logo</p>
                  <img src={resolveBackendAssetUrl(umModal.data.logoPath)} alt="Current" className="um-logo-preview" />
                </div>
              )}
              <label className="admin-field">
                <span className="admin-card__label">Upload New Logo</span>
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={handleUmLogoFileChange} />
                {umLogoPreview && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p className="admin-card__label">New Logo Preview</p>
                    <img src={umLogoPreview} alt="New preview" className="um-logo-preview" />
                  </div>
                )}
              </label>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving || !umLogoFile} onClick={() => {
                setUmConfirm({
                  title: 'Update Logo',
                  message: 'Are you sure you want to replace the current logo?',
                  onConfirm: async () => { closeUmConfirm(); await handleUploadLogo(); },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Update Logo</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Color Theme Modal */}
      {umModal.type === 'changeColor' && umModal.data && (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeUmModal} role="presentation" />
          <div className="admin-dialog__panel" style={{ maxWidth: 420 }}>
            <div className="admin-dialog__header">
              <h3>Change Color — {umModal.data.acronym}</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmModal}><X size={18} /></button>
            </div>
            {umModalError && <div className="admin-alert is-error">{umModalError}</div>}
            <div className="admin-dialog__body">
              <div className="admin-field">
                <span className="admin-card__label">ID Color Theme</span>
                <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                  {Object.entries(COLOR_THEMES).map(([key, val]) => (
                    <div
                      key={key}
                      title={key.charAt(0).toUpperCase() + key.slice(1)}
                      onClick={() => setUmModalForm((f) => ({ ...f, colorTheme: key }))}
                      style={{
                        width: 36, height: 36, borderRadius: '50%', cursor: 'pointer',
                        background: val.headerGradient,
                        border: (umModalForm.colorTheme || 'red') === key ? '3px solid #111' : '2px solid #d1d5db',
                        transition: 'border 0.15s',
                      }}
                    />
                  ))}
                </div>
                <span className="admin-muted" style={{ fontSize: '0.85rem', marginTop: '6px' }}>Selected: {(umModalForm.colorTheme || 'red').charAt(0).toUpperCase() + (umModalForm.colorTheme || 'red').slice(1)}</span>
              </div>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmModal}>Cancel</button>
              <button type="button" className="admin-button is-primary" disabled={umSaving} onClick={() => {
                setUmConfirm({
                  title: 'Change Color Theme',
                  message: `Change color theme of ${umModal.data.acronym} to "${(umModalForm.colorTheme || 'red').charAt(0).toUpperCase() + (umModalForm.colorTheme || 'red').slice(1)}"?`,
                  onConfirm: async () => {
                    closeUmConfirm();
                    setUmSaving(true);
                    try {
                      await updateAffiliationColorTheme(umModal.data.id, umModalForm.colorTheme);
                      setUmAffiliations((prev) => prev.map((a) =>
                        a.id === umModal.data.id ? { ...a, colorTheme: umModalForm.colorTheme } : a
                      ));
                      closeUmModal();
                    } catch (err) {
                      setUmModalError(err?.response?.data?.message ?? 'Unable to update color theme.');
                    } finally {
                      setUmSaving(false);
                    }
                  },
                });
              }}>{umSaving ? <Loader2 size={16} className="spin" /> : null} Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Union Management Confirmation Modal */}
      {umConfirm && (
        <div className="admin-dialog" role="dialog" aria-modal="true" style={{ zIndex: 60 }}>
          <div className="admin-dialog__backdrop" onClick={closeUmConfirm} role="presentation" />
          <div className="admin-dialog__panel" style={{ maxWidth: 420 }}>
            <div className="admin-dialog__header">
              <h3>{umConfirm.title || 'Confirm'}</h3>
              <button type="button" className="admin-dialog__close" onClick={closeUmConfirm}><X size={18} /></button>
            </div>
            <div className="admin-dialog__body">
              <p>{umConfirm.message}</p>
            </div>
            <div className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--secondary" onClick={closeUmConfirm}>Cancel</button>
              <button type="button" className="admin-button is-primary" onClick={umConfirm.onConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      </div>
    );
}
